import moment from 'moment'
import * as React from 'react'

import * as env from '../env'
import * as schema from '../schema'
import * as utils from '../utils'
import { Customer, MediaItem, MediaKind } from '../schema'
import { Writable, isBetween, range, stringIsInt } from '../utils'

export type FieldToTrait<T> = Partial<Readonly<Record<schema.CustomerField, T>>>

export const fieldToLabel: FieldToTrait<string> = {
  //#region airConditioner
  air_conditioner: 'Air Conditioner',
  air_conditioner_current_tons: 'How many tons is your current AC?',
  air_conditioner_furnance: 'Will you include the furnance?',
  air_conditioner_new_tons: 'How many tons will the new AC be? ',
  air_conditioner_qty: 'How many AC units?',
  air_conditioner_replace_ducts: 'Are you replacing ducts?',
  air_conditioner_unit_type: 'What type AC unit are you going to install?',
  air_conditioner_working: 'Do you currently have an AC?',
  air_conditioner_swamp_cooler_use: 'Are you using a swamp cooler?',
  air_conditioner_swamp_cooler_use_remove:
    'Would you be removing any swamp coolers?',
  //#endregion
  //#region atticFan
  attic_fan_quantity: 'How many?',
  attic_fan: 'Attic Fan',
  //#endregion
  //#region atticInsulation
  attic_insulation_type: 'What type of insulation?',
  attic_insulation: 'Attic Insulation',
  attic_insulation_how_much: 'Square footage to be insulated',
  //#endregion
  attic_square_footage: 'Attic square footage',
  //#region battery
  battery_type: 'Select battery type',
  battery_size: 'Select battery size',
  battery: 'Battery',
  //#endregion

  //#region constructionWork
  construction_work: 'Big Additions / New Construction',
  //#endregion

  buildingType: 'Building type',

  //#region batteryMultiple
  battery_multiple: 'Batteries',
  battery_multiple_bank_location: 'Battery type',
  battery_multiple_customer_material: 'Is homeowner providing material?',
  battery_multiple_distance_to_main_panel: 'Distance from bank to main panel',
  battery_multiple_how_many: 'How many batteries?',
  //#endregion

  //#region
  cash_amount: 'Cashback amount',
  cash_pool_pumps: 'Pool Pumps',
  cash_heat_pumps: 'Heat Pumps',
  cash_whole_house_fans_or_ventilation: 'Whole House Fans / Ventilation',
  cash_efficient_boilers: 'Efficient Boilers',
  cash_geothermal_heat_pumps: 'Geothermal Heat Pumps',
  cash_radiant_floors: 'Radiant Floors',
  cash_water_saving_fixtures: 'Water Saving Fixtures',
  cash_solar_water_heaters: 'Solar Water Heaters',
  cash_on_demand_or_tankless_hot: 'On Demand / Tank-less Hot Water Heater',
  cash_efficient_water_heaters: 'Efficient Water Heaters',
  cash_artificial_turf: 'Artificial Turf',
  cash_irrigation_systems: 'Irrigation Systems',
  cash_windows: 'Windows',
  cash_air_conditioner: 'Air Conditioner',
  cash_floor_insulation: 'Floor Insulation',
  cash: 'Cash',
  //#endregion
  createdAt: 'Created at',
  // Was 'Crm Id', which Jaron circled on 2026-08-25 asking "What does this
  // mean?". The field is real and useful — it holds the customer's contact ID
  // in the CRM, and tapping it opens their record there (see `useCRMUrl`) — so
  // the answer was never to remove it, only to stop naming it like a database
  // column.
  crm_id: 'CRM contact (tap to open)',
  //#region custom
  custom_eight_fields_name: 'Name',
  custom_eight_fields: 'Custom 8',
  custom_fields_name: 'Name',
  custom_fields: 'Custom 1',
  custom_five_fields_name: 'Name',
  custom_five_fields: 'Custom 5',
  custom_four_fields_cost: 'Cost',
  custom_four_fields_name: 'Name',
  custom_four_fields: 'Custom 4',
  custom_nine_fields_name: 'Name',
  custom_nine_fields: 'Custom 9',
  custom_seven_fields_name: 'Name',
  custom_seven_fields: 'Custom 7',
  custom_six_fields_cost: 'Cost',
  custom_six_fields_name: 'Name',
  custom_six_fields: 'Custom 6',
  custom_ten_fields_cost: 'Cost',
  custom_ten_fields_name: 'Name',
  custom_ten_fields: 'Custom 10',
  custom_three_fields_name: 'Name',
  custom_three_fields: 'Custom 3',
  custom_two_fields_name: 'Name',
  custom_two_fields: 'Custom 2',
  //#endregion
  customerAddress: 'Customer address',
  customerEmail: 'Customer email',
  customerName: 'Customer name',
  customerPhone: 'Customer phone',
  customerPhoneAlt: 'Alt phone number (opt)',
  date: 'Created at',

  can_light_new: 'Can lights',
  can_light_new_how_many: 'How many can lights will be installed?',
  can_light_new_will_remove: 'Will you be removing any existing can lights?',
  can_light_new_will_remove_how_many: 'How many can lights will be removed?',
  can_light_new_high_ceiling: 'Are any of the ceilings above 12ft?',
  can_light: 'Can lights (old)',

  ceiling_fan: 'Ceiling Fan',

  derate: 'Derate',
  derate_cost: 'Cost',
  duct_insulation_cost: 'Cost',
  duct_insulation: 'Duct Insulation',
  duct_repair_quantity: 'How many?',
  duct_repair: 'Duct Repair',
  //#region duct_replacement
  duct_replacement: 'Duct Replacement',
  duct_replacement_qty: 'How many ducts will be replaced?',
  //#endregion duct_replacement
  duct_seal_cost: 'Cost',
  duct_seal: 'Duct Seal',
  flat_roof_panels: 'Flat Roof Panels',
  flat_roof_panels_cost: 'Cost',
  flat_roof_panels_how_many: 'How many?',
  globalNotes: 'Global customer notes',
  glow: 'Protocol Fee (Glow Gold)',
  glow_price_per_watt: 'Price Per Kw/h from the utility bill',
  glow_power_output_mw: 'System Size in Kw',

  home_run: 'New Home Runs',
  home_run_ft: 'How many feet?',

  incentive_adders_description: 'Incentive/Adders Description',

  inverter_swap: 'Inverter',
  inverter_swap_qty: 'How many inverters?',
  inverter_swap_customer_material: 'Is homeowner providing material?',
  inverter_swap_size: 'Size',
  inverter_swap_distance_to_mpu: 'Distance to main panel',

  main_panel_location: 'Where is the main panel?',
  main_panel_upgrade: 'Main Panel Upgrade (MPU)',
  main_panel_upgrade_rating: 'Rating (in amps)',
  main_panel_upgrade_needed_or_requested: 'Is needed or requested by customer?',
  main_panel_upgrade_installation_company: 'Installer',

  mini_split: 'Mini Split',
  mini_split_ton1: 'Mini Split System 1Ton',
  mini_split_ton2: 'Mini Split System 2Ton',
  mini_split_cost: 'Cost',
  new_ducts_description: 'Description',
  new_ducts_quantity: 'How many?',
  new_ducts_replace: 'Replacing ducts?',
  new_ducts: 'New Ducts',
  //#region new_windows
  new_windows_areas: 'What areas?',
  new_windows_color: 'Window colors',
  new_windows_cost: 'Cost',
  new_windows_grid: 'Grid windows?',
  new_windows_quantity: 'Total Number of Windows',
  new_windows_sqft_each_window: 'Sqft of each window?',
  new_windows_sliding_glass_sqft: 'Feet of each sliding glass door?',
  new_windows_sliding_glass: 'Sliding glass doors included?',
  new_windows_sliding_glass_how_many:
    'Total Number of Glass Sliding Doors included:',
  new_windows_sqft: 'How Much Square Footage?',
  new_windows_replace: 'Are you replacing the windows on the whole house?',
  new_windows_replace_custom:
    'What areas of home windows will be installed in detail?',
  new_windows: 'Windows',
  new_windows_california_city: 'Choose a city',
  new_windows_sliding_glass_size: 'Sliding glass door Size',
  newWindows: 'Windows & Sliding Glass Doors',
  //#endregion new_windows
  //#region nirvanaPanels
  nirvana_panels: 'Solar Panels',
  nirvana_panels_panel_scenario: 'Main panel scenario',
  nirvana_panels_how_many: 'How many panels?',
  nirvana_panels_distance_to_main_panel: 'Distance to main panel',
  //#endregion

  outlet: 'Outlets',

  pool_pump_cost: 'Cost',
  pool_pump: 'Pool Pump',
  //#region protection
  protection: 'Surge Protection',
  protection_which: 'Which type of protection?',
  //#endregion protection
  //#region roof
  roof_cost: 'Cost',
  roof_california_city: 'Choose a city',
  roof_square_footage: 'Roof squares',
  roof_fascia_included: 'Are we including fascia?',
  roof_fascia_included_square_footage:
    'How many linear feet are we going to included?',
  roof_layover_or_tear: 'Choose type of roof work',
  roof_tear_material: 'Material duration',
  roof_patio_included: 'Patio/ flat roof included?',
  roof_patio_how_many: 'How many squares?',
  roof_pitch: 'What is the pitch of the roof?',
  roof_plywood_under_tile: 'Is there plywood under the roof tile?',
  roof_plywood_replaced: 'Will you be replacing all the plywood?',
  roof_plywood_replaced_square: 'How many squares?',
  roof_shingles_or_tiles: 'Shingles or Tiles?',
  roof_will_add_license: 'Pulling permits?',
  roof_commission_add_up: 'Commission add up',
  roof: 'Roof',
  // #endregion roof

  //#region security
  security_alarm_payout: 'Alarm payout',
  security_cams: 'CCTV Cameras',
  security_cams_is_6: 'How many cameras?',
  security_home_automation: 'Home Automation',
  security_monitoring_enabled: 'Monitoring ($9.99/mo)',
  security_system: 'Security System',
  security_tv: 'TVs',
  security_wifi_cameras: 'WiFi Camera',
  //#endregion

  service_and_repair: 'Service and Repairs',
  service_and_repair_hours: 'Hours',

  small_system_size: 'System size',
  small_system_extra_batteries_qty: 'How many?',
  small_system_extra_batteries_will: 'Will you install additional batteries?',

  // smart_home: 'Smart Home',

  // Closer — field label changed "EcoHome rep (closer)" -> "Sales Rep" per
  // Monday 12896824165 (Jaron, 2026-08-25).
  homeRep: env.CODENAME === 'nirvana' ? 'Nirvana representative' : 'Sales Rep',
  smart_thermostat_how_many: 'How many?',
  smart_thermostat: 'Smart Thermostat',
  solar_tax: 'Solar Tax Consultant',
  solarCompany: 'State',
  products: 'Products',
  solarEmail: 'Rep email',
  // Setter
  solarRep: 'Rep (setter)',
  sort_key: 'Last update',
  sub_panel_upgrade: 'Sub Panel Upgrade',
  ssn: 'SSN',
  dob: 'Date of birth',
  system_size: 'System size',
  small_system: 'Small System',
  homeStoryNumber: 'One or two story building?',
  yearHomeBuilt: 'Year home was built',
  panel_removal: 'Panel Removal',
  panel_removal_how_many: 'How many panels do you need to remove?',

  //#region nirvanaChecklist
  checklist_nirvana_home_owners_info:
    "Home owner's information (Full Name, address and email)",
  checklist_nirvana_product_pics:
    'Pictures from ground to roof of where product will be placed or installed',
  checklist_nirvana_main_panel_pics: 'Picture of main panel (if applicable)',
  checklist_nirvana_main_panel_inside_pics:
    'Picture of inside of main panel (to see if there is space to add breakers)',
  checklist_nirvana_utility_bill: 'Picture of utility bill (if applicable)',
  //#endregion

  //#region checkList
  attach_thermal_camera: 'Attach thermal camera to the phone',

  take_picture_of_home: 'Take picture of home',
  get_debts_report: 'Get debts report',

  perform_energy_assessment: 'Perform energy assessment',
  /**/ use_thermal_camera: 'Use thermal camera',
  /**/ take_quiz: 'Take survey',
  /**/ use_room_that_gets_hot: 'Room that gets hot',
  /**/ use_smoke_pen: 'Use smoke pen',

  take_pictures: 'Take pictures',

  fill_out_prices: 'Fill out prices',

  verify_pricing_or_ppw: 'Verify pricing/PPW (info)',

  show_ecohome_report: `Show ${env.HOLDING_NAME} report`,

  show_solo_report: 'Show Solo report',

  /**/ /**/

  run_credit: 'Run credit',

  change_to_correct_email: 'Change to correct email',

  sign_financing_agreement: 'Sign financing agreement (info)',
  /**/ sign_cosigner: 'Sign co-signer',

  submit_install_docs_in_solo: 'Submit install docs in Solo (info)',
  /**/ counter_sign_rep_install_doc: 'Counter-sign rep install doc (info)',
  /**/ sign_customer_install_docs: 'Sign customer install docs (info)',

  ecohome_report: `${env.HOLDING_NAME} report`,
  /**/ make_sure_ecohome_and_solo_match:
    'Make Sure Ecohome/Solo #s match (info)',
  /**/ take_picture_of_utility_bill: 'Take picture of utility bill',
  /**/ take_picture_of_id: 'Take picture of ID',
  /**/ take_pictures_of_meter: 'Take pictures of meter (example)',
  /**/ submit_detailed_notes_for_efficiencies:
    'Submit detailed notes for efficiencies (info)',
  /**/ review_notes_with_customers: 'Review notes w/ customers',
  /**/ sign_ecohome_report: `Sign ${env.HOLDING_NAME} report`,
  /**/ get_google_review_and_or_pic_with_customer:
    'Get a 5-star Google Review from the client',
  /**/ take_pic_with_customer: 'Take picture with customer',
  /**/ offer_incentives_for_referrals: 'Offer incentives for referrals',

  update_notes_on_HL: 'Update notes on HL',

  report_sale_to_slack: 'Report sale to Slack!',
  //#endregion
  // Roofing Claiming

  //#region roofClaims
  roof_claiming_date: 'Date',
  roof_claiming_homeowner: 'Homeowner',
  roof_claiming_phone: 'Phone',
  roof_claiming_phone_alt: 'Alt Phone',
  roof_claiming_email: 'Email',
  roof_claiming_address: 'Address',
  roof_claiming_city: 'City',
  roof_claiming_zip: 'Zip',
  roof_claiming_deductible: 'Deductible',
  roof_claiming_shingle_manufacturer: 'Shingle Manufacturer',
  roof_claiming_style: 'Style',
  roof_claiming_color: 'Color',
  roof_claiming_drip_color: 'Drip Color',

  roof_claiming_tear_off_layers_shingles: 'Tear off all layers of shingles',
  roof_claiming_install_felt: 'Install Felt',
  roof_claiming_close_valleys: 'Close Valleys ',
  roof_claiming_ridges_color_coordinated: 'Ridges Color Coordinated',
  roof_claiming_install_new_flashings: 'Install New Flashings',
  roof_claiming_replace_ventilation: 'Replace ventilation as needed',
  roof_claiming_install_nails: 'Install nails per code',
  roof_claiming_clean_job_waste_gutters: 'Clean job waste from gutters',
  roof_claiming_two_year_workmanship_warranty: '2 year workmanship warranty ',
  roof_claiming_magnetic_sweep_property:
    'Magnetic sweep property after install ',

  roof_claiming_age: 'Age',
  roof_claiming_layers: 'Layers',
  roof_claiming_predominate_pitch: 'Predominate Pitch',

  roof_claiming_pipe_jack: 'Pipe Jack (QTY)',
  roof_claiming_chimney_flashing: 'Chimney Flashing (QTY)',
  roof_claiming_chimney_cap: 'Chimney Cap (QTY)',
  roof_claiming_digital_satellite: 'Digital Satellite (QTY)',
  roof_claiming_gas_cap: 'Gas Vent/Rain Cap (QTY)',
  roof_claiming_other_one: 'Other',

  roof_claiming_turtle_vent: 'Turtle Vent (QTY)',
  roof_claiming_ridge_vent: 'Ridge Vent (QTY)',
  roof_claiming_turbine_vent: 'Turbine Vent (QTY)',
  roof_claiming_power_attic_vent: 'Power Attic Vent (QTY)',
  roof_claiming_other_two: 'Other',
  roof_claiming_other_three: 'Other',

  /**
   * To be removed
   * @deprecated
   */
  roof_claiming_notes: 'Roof claim notes',
  /**
   * To be removed
   * @deprecated
   */
  roof_claiming_documents_notes: 'Notes',

  roof_claiming_date_loss: 'Date of Loss',
  roof_claiming_insurance_company: 'Insurance Company',
  roof_claiming_claim: 'Claim #',

  roof_claiming_client_sign: 'Client(s) Signature',
  roof_claiming_company_rep_sign: 'Authorized Signature',

  roof_claiming_printed_name: 'Printed Name',
  roof_claiming_completion_client_sign: 'Client(s) Signature',
  roof_claiming_company_rep_sign_date: 'Date',
  roof_claiming_completion_client_sign_date: 'Date',
  //#endregion

  // End Roofing Claiming

  //#region glowQualify
  qualify_glow_birth_date: 'Birth Date',
  qualify_glow_city: 'City',
  qualify_glow_email: 'Email',
  qualify_glow_first_name: 'First Name',
  qualify_glow_last_name: 'Last Name',
  qualify_glow_phone_number: 'Phone Number',
  qualify_glow_ssn: 'Last 4 Digits or Full SSN',
  qualify_glow_state: 'State',
  qualify_glow_street: 'Street',
  qualify_glow_zip: 'Zip',
  //#endregion

  //#region glowIncentive
  incentive_glow_carbon_credits: 'Carbon Credits Produce',
  incentive_glow_cash: 'Glow Cash',
  incentive_glow_tax_deduction: 'Tax Deduction',
  incentive_glow_federal_tax_credit: 'Federal Tax Credit',
  //#endregion
}

/**
 * Will only contain one dependent for now.
 */
export const yesNoToDependent: FieldToTrait<[schema.CustomerField]> = {
  air_conditioner_swamp_cooler_use: ['air_conditioner_swamp_cooler_use_remove'],
  air_conditioner_working: ['air_conditioner_current_tons'],
  can_light_new_will_remove: ['can_light_new_will_remove_how_many'],
  new_windows_replace: ['new_windows_replace_custom'],
  new_windows_sliding_glass: ['new_windows_sliding_glass_sqft'],
  roof_fascia_included: ['roof_fascia_included_square_footage'],
  roof_patio_included: ['roof_patio_how_many'],
  roof_plywood_replaced: ['roof_plywood_replaced_square'],
  small_system_extra_batteries_will: ['small_system_extra_batteries_qty'],
}

export const fieldToDependency: FieldToTrait<schema.CustomerField> = (() => {
  const res = {}
  // @ts-ignore
  for (const [dependency, [dependent]] of utils.entries(yesNoToDependent)) {
    // @ts-ignore
    res[dependent] = dependency
  }
  return res
})()

/**
 * Sub-inputs are guaranteed to be 1 or 2 in length. Inputs should be rendered
 * in the order of this object's keys.
 */
export const efficiencyToInputs: FieldToTrait<schema.CustomerFields> = {
  //#region security
  security_cams: ['security_cams_detail'],
  security_wifi_cameras: ['security_wifi_cameras_detail'],
  security_system: ['security_system_detail'],
  security_home_automation: ['security_home_automation_detail'],
  security_tv: ['security_tv_detail'],
  // smart_home: ['smart_home_detail'],
  //#endregion
  attic_insulation: ['attic_insulation_type', 'attic_insulation_how_much'],
  duct_seal: [],
  inverter_swap: [
    'inverter_swap_qty',
    'inverter_swap_size',
    'inverter_swap_distance_to_mpu',
    'inverter_swap_customer_material',
    'main_panel_location',
  ],
  roof: [
    'roof_square_footage',
    'roof_pitch',
    'roof_layover_or_tear',
    'roof_layers_how_many',
    'roof_tear_material',
    'roof_fascia_included',
    'roof_patio_included',
    'roof_plywood_under_tile',
    'roof_plywood_replaced',
    'roof_will_add_license',
    'roof_commission_add_up',
  ],
  new_windows: [
    'new_windows_sqft_each_window',
    'new_windows_color',
    'new_windows_grid',
    'new_windows_sliding_glass',
    'new_windows_replace',
  ],
  air_conditioner: [
    'air_conditioner_qty',
    'air_conditioner_working',
    'air_conditioner_replace_ducts',
    'air_conditioner_swamp_cooler_use',
    'air_conditioner_new_tons',
    'air_conditioner_unit_type',
    'air_conditioner_furnance',
  ],
  battery: [
    'main_panel_upgrade_installation_company',
    'battery_type',
    'battery_size',
  ],
  battery_multiple: [
    'battery_multiple_bank_location',
    'battery_multiple_distance_to_main_panel',
    'battery_multiple_how_many',
    'battery_multiple_customer_material',
  ],
  nirvana_panels: [
    'nirvana_panels_panel_scenario',
    'nirvana_panels_distance_to_main_panel',
    'nirvana_panels_how_many',
  ],
  protection: ['protection_which'],
  can_light_new: [
    'can_light_new_how_many',
    'can_light_new_will_remove',
    'can_light_new_high_ceiling',
  ],
  can_light: ['can_light_detail'],
  ceiling_fan: ['ceiling_fan_detail'],
  construction_work: [],
  home_run: ['home_run_ft'],
  main_panel_upgrade:
    env.CODENAME === 'nirvana'
      ? ['main_panel_upgrade_rating']
      : [
          'main_panel_upgrade_installation_company',
          'main_panel_upgrade_needed_or_requested',
        ],
  derate: ['main_panel_upgrade_installation_company'],
  mini_split:
    env.CODENAME === 'nirvana'
      ? ['roof_shingles_or_tiles', 'mini_split_tons']
      : ['mini_split_tons'],
  smart_thermostat: ['smart_thermostat_how_many'],
  attic_fan: ['attic_fan_quantity'],
  pool_pump: [],
  duct_replacement: ['duct_replacement_qty'],
  duct_repair: ['duct_repair_quantity'],
  // Let us disable for now, for simplicity of the loan process
  // cash: ['cash_amount'],
  service_and_repair: ['service_and_repair_hours'],
  // solar_tax: [],
  small_system: [
    'main_panel_upgrade_installation_company',
    'small_system_size',
    'small_system_extra_batteries_will',
  ],
  flat_roof_panels: ['flat_roof_panels_how_many'],
  outlet: ['outlet_detail'],
  panel_removal: ['panel_removal_how_many'],
  // sub_panel_upgrade: [],
  // glow: ['glow_price_per_watt', 'glow_power_output_mw'],
  custom_fields: ['custom_fields_qty', 'custom_fields_name'],
  custom_two_fields: ['custom_two_fields_qty', 'custom_two_fields_name'],
  custom_three_fields: ['custom_three_fields_qty', 'custom_three_fields_name'],
  custom_four_fields: ['custom_four_fields_qty', 'custom_four_fields_name'],
  custom_five_fields: ['custom_five_fields_qty', 'custom_five_fields_name'],
  custom_six_fields: ['custom_six_fields_qty', 'custom_six_fields_name'],
  custom_seven_fields: ['custom_seven_fields_qty', 'custom_seven_fields_name'],
  custom_eight_fields: ['custom_eight_fields_qty', 'custom_eight_fields_name'],
  custom_nine_fields: ['custom_nine_fields_qty', 'custom_nine_fields_name'],
  custom_ten_fields: ['custom_ten_fields_qty', 'custom_ten_fields_name'],
}

// TODO: Do this via database
if (env.IS_WHITE_LABEL) {
  if (env.CODENAME === 'glow') {
    const allFields = utils.keys(efficiencyToInputs)
    const nonGlowFields = allFields.filter(
      (field) => !schema.glowFields.includes(field),
    )
    for (const field of nonGlowFields) {
      delete efficiencyToInputs[field]
    }
  } else if (env.CODENAME === 'nirvana') {
    const allFields = utils.keys(efficiencyToInputs)
    const nonNirvanaFields = allFields.filter(
      (field) => !schema.nirvanaFields.includes(field),
    )
    for (const field of nonNirvanaFields) {
      delete efficiencyToInputs[field]
    }
  } else {
    for (const field of schema.mainOnlyFields) {
      delete efficiencyToInputs[field]
    }
  }
} else {
  for (const field of schema.nirvanaOnlyFields) {
    delete efficiencyToInputs[field]
  }
  for (const [efficiency, inputs] of utils.entries(efficiencyToInputs)) {
    ;(efficiencyToInputs as Writable<typeof efficiencyToInputs>)[efficiency] =
      inputs!.filter((input) => !schema.nirvanaOnlyFields.includes(input))
  }
}

export const inputToEfficiency: FieldToTrait<schema.CustomerField> = (() => {
  const res: Writable<FieldToTrait<schema.CustomerField>> = {}

  for (const [efficiency, inputs] of utils.entries(efficiencyToInputs)) {
    for (const input of inputs!) res[input] = efficiency
    res[(efficiency + '_notes') as schema.CustomerField] = efficiency
    // res[efficiency] = efficiency
  }

  // @ts-ignore Typescript whack here
  for (const [input, [subInput]] of utils.entries(yesNoToDependent)) {
    // @ts-ignore Typescript whack here
    res[subInput] = res[input]
  }

  return res
})()

const rowableEfficiencies = Object.fromEntries(
  schema.rowableFields.map((field) => {
    return [efficiencyToInputs[field as schema.CustomerField]!, true]
  }),
) as FieldToTrait<boolean>

const efficiencyToRowable = Object.fromEntries(
  schema.rowableFields.map((field) => {
    return [efficiencyToInputs[field as schema.CustomerField]!, field]
  }),
) as FieldToTrait<schema.CustomerField>

export const efficiencyKeys = utils.keys(efficiencyToInputs)
export const nonCustomEfficiencyKeys = efficiencyKeys.filter(
  (k) => !k.startsWith('custom_'),
)

export const getFieldLabel = (
  field: keyof Customer,
  customer?: schema.Customer,
): string => {
  if (field === 'homeRep' && env.CODENAME === 'glow') {
    return 'Sales Representative'
  }
  if (field === 'roof_layers_how_many') {
    const roofingType = customer?.roof_layover_or_tear

    if (roofingType === schema.RoofWorkType.Layover) {
      return 'How many layers?'
    }
    if (roofingType === schema.RoofWorkType.ReRoofShingles) {
      return 'Current roof layers'
    }
    if (roofingType === schema.RoofWorkType.ReRoofFlatRoof) {
      return 'Current roof layers'
    }
    if (roofingType === schema.RoofWorkType.ReRoofWoodShake) {
      return 'Current roof layers'
    }
    return ''
  }

  if (field.startsWith('custom_')) {
    if (field.endsWith('_qty')) {
      return 'Qty'
    }
  }

  const maybeLabel = fieldToLabel[field]

  if (maybeLabel) return maybeLabel
  if (field.endsWith('customer_cost')) return 'Customer cost'
  if (field.endsWith('cost')) return 'Cost'
  if (field.endsWith('extra_notes')) return 'Extra notes'
  if (field.toLowerCase().includes('notes')) return 'Notes'
  return field
}

/**
 * A dollar amount as it should appear anywhere a customer reads it.
 *
 * The same `Intl.NumberFormat('en-US', …)` block is written out 30 times
 * across the report and the contracts, plus two local `money` helpers. That
 * duplication is not cosmetic: the Job Summary Total was rendering
 * `${n.toFixed(2)}` and came out "$43295.00" while every amount around it read
 * "$19,500", and nobody noticed because there was no single place to look.
 * V62 - a shared display rule belongs here, not beside its first caller.
 *
 * The two former local copies now call this. The 30 inline blocks are each a
 * contract surface that needs eyes on it before being touched, so they move on
 * their own ticket rather than in the change that found them.
 */
export const money = (amount: number): string =>
  Intl.NumberFormat('en-US', {
    currency: 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: 'currency',
  }).format(amount)

/**
 * The dashed heading that opens a security section in a contract's Scope of
 * Work, e.g. `--------------- CCTV Cameras ---------------`.
 *
 * The five headings used to be hardcoded strings sitting beside the collapsed
 * name, which came from `getFieldLabel`. Four pairs agreed. The fifth did not:
 * the CCTV section read "Security Camera" expanded and "CCTV Cameras"
 * collapsed, so collapsing a section on a signed contract renamed it. Deriving
 * the heading from the same label is what stops that happening again - the two
 * cannot disagree if there is only one of them.
 *
 * Padded to a constant width so the headings line up under each other, which
 * is what the old strings were reaching for by hand: "TVs" carried 21 dashes a
 * side against everyone else's 15, and "WiFi Camera" carried 15 where it
 * needed 17.
 */
export const scopeSectionHeader = (field: schema.CustomerField): string => {
  const label = getFieldLabel(field)
  const dashes = '-'.repeat(
    Math.max(3, Math.floor((47 - label.length - 2) / 2)),
  )

  return `${dashes} ${label} ${dashes}`
}

/**
 * Reads a customer's `<field>_notes` and trims it.
 *
 * The `|| ''` is not redundant even though the type says `string`. A customer
 * comes out of `makeSelectCustomer` as the raw Firebase document, never merged
 * with `createEmptyCustomer`'s defaults, so a document written before a field
 * existed simply has no key for it and the type is a lie. Reading it and
 * calling `.trim()` throws, which takes the whole report page down rather than
 * degrading - the same failure `fieldToEfficiency` produced on 2026-08-31.
 *
 * Measured against all 7,151 customers: 80 are missing a `_notes` key for an
 * efficiency they have sold, mostly `main_panel_upgrade` (53), on documents
 * from 2021-22.
 *
 * Lives here rather than as a guard repeated at each of the five read sites, so
 * the next surface that reads a notes field cannot reintroduce the crash - V62.
 */
export const customerNotes = (
  customer: schema.Customer,
  field: schema.CustomerField,
): string =>
  ((customer[`${field}_notes` as schema.CustomerField] as string) || '').trim()

// Jaron requested his name never appear on a rendered contract (Slack,
// 2026-07-09) — Torrin shows in his place instead. Display-only: does not
// touch the underlying `homeRep` customer field or Firebase data. Excludes
// NirvanaContract.tsx (Daniel's exclusive surface, see B44).
export const displayHomeRepOnContract = (homeRep: string): string =>
  homeRep.toLowerCase().includes('jaron') ? 'Torrin' : homeRep

/**
 * The media items that have somewhere to load from.
 *
 * A record that never finished uploading has no URL — `getMediaItemSrc`
 * returns an empty string for it, and rendering that produces a black,
 * sourceless player: no image, no error, not even a failed request, because
 * with no src the browser never asks for anything.
 *
 * Two of the six videos on the test customer are in that state, and the report
 * is the document the customer is handed. Dropped rather than shown with a
 * placeholder: a customer reading their own report has nothing to do with
 * "this video is missing", and a blank box explains nothing while making the
 * document look broken. The rep still sees the item in the app.
 *
 * Lives here rather than in the report component because it is a rule about
 * media records, not about a grid — and because it is the kind of thing that
 * needs a test that runs.
 */
export const renderableMedia = (
  items: readonly schema.MediaData[],
): schema.MediaData[] => items.filter((item) => !!item.url)

export const getMediaItemSrc = (mediaItem: MediaItem): string => {
  if (mediaItem.status === 'uploaded') {
    return (
      'https://firebasestorage.googleapis.com/v0/b/' +
      env.commonEnv.FB_PROJECT_ID +
      '.' +
      env.commonEnv.FB_ST_BUCKET_SUFFIX +
      '/o/' +
      mediaItem.order_id +
      '%2F' +
      // TODO: thumbnail
      mediaItem.name +
      `?alt=media&token=dummy`
    )
  }

  // A record that is not uploaded yet points at a file on the device that took
  // it, and that only holds while `path` is set. Concatenating an empty path
  // produced the bare string 'file://', which is not a location at all - handing
  // that to <Image> took the whole screen down on iOS, and because the record
  // lives in the database the screen kept going down on every later visit.
  //
  // A record with no path is now simply unrenderable, which is the truth: the
  // pixels exist on another device, or nowhere.
  if (!mediaItem.path) {
    return ''
  }

  return 'file://' + mediaItem.path
}

export const qualifyGlowPrimaryApplicantInputs: readonly schema.CustomerFields[] =
  [
    ['qualify_glow_first_name', 'qualify_glow_last_name'],
    ['qualify_glow_phone_number', 'qualify_glow_email'],
    ['qualify_glow_ssn', 'qualify_glow_birth_date'],
  ]

export const qualifyAddressInfoInputs: readonly schema.CustomerFields[] = [
  ['qualify_glow_street'],
  ['qualify_glow_city', 'qualify_glow_state', 'qualify_glow_zip'],
]

export const normalizeThumbnail = (
  customerID: string,
  thumbnail: string,
): string => {
  if (thumbnail === 'default_house.png') {
    return ''
  }
  if (thumbnail.length === 0) {
    return ''
  }
  if (thumbnail.startsWith('http')) {
    return thumbnail
  }
  if (thumbnail.startsWith('data:image/')) {
    return thumbnail
  }
  // Base64 jpg
  if (thumbnail.startsWith('/9j/')) {
    return 'data:image/jpeg;base64,' + thumbnail
  }
  // Base64 png
  if (thumbnail.startsWith('iVBO')) {
    return 'data:image/png;base64,' + thumbnail
  }

  return (
    'https://firebasestorage.googleapis.com/v0/b/' +
    env.commonEnv.FB_PROJECT_ID +
    '.' +
    env.commonEnv.FB_ST_BUCKET_SUFFIX +
    '/o/' +
    customerID +
    '%2F' +
    thumbnail +
    '?alt=media&token=69e431fa-59ad-497a-b700-a3aebcc68fc6'
  )
}

export const mediaToLabel: Partial<Record<MediaKind, string>> = {
  adders: 'Adjustment screenshots',
  electricity: 'Electricity bill',
  owner_house: 'House front view (showing address)',
  insulation: 'Attic',
  roof: 'Roof',
  windows: 'Windows',
  problem: 'Other areas',
  hvac_system: 'Hvac',
  id: 'ID picture',
  mini_split_surroundings: 'Mini split surroundings (wall & floor)',
  other_documents: 'Other documents',
  security_cam0: 'Cam angle #1',
  security_cam1: 'Cam angle #2',
  security_cam2: 'Cam angle #3',
  security_cam3: 'Cam angle #4',
  security_cam4: 'Cam angle #5',
  security_cam5: 'Cam angle #6',
  security_cam6: 'Cam angle #7',
  security_cam7: 'Cam angle #8',
  security_camera_locations: 'Camera Locations',
  security_camera_locations_s4: 'S4 Camera locations',
  security_camera_locations_s3: 'S3 Camera locations',
  security_camera_locations_s2: 'S2 Camera locations',
  security_camera_locations_indoor: 'Indoor Camera locations',
  security_doorbell_locations: 'Doorbell Locations',
  security_360_house_view: '360 House View',
  security_attic: 'Attic Access',
  security_router_location: 'Router Location',
  security_post_camera_installation: 'Post Camera Installation',
  // Renamed from 'Post Alarm Installation' (Monday 12824335937). Jaron,
  // verbatim: "todo lo que esta como alarma tiene que aparecer como smart home,
  // no puede aparecer que vendemos alarmas". Luis's rename covered section
  // titles; this row label sat inside the renamed Smart Home section still
  // reading "Alarm" on screen.
  //
  // "Panel" rather than "Smart Home" because the row next to it is already
  // "Post Camera Installation" - two rows both called Post Smart Home
  // Installation would be worse than the problem. WORDING NEEDS JARON'S OK: it
  // is a label a rep reads every day, and this is a judgement call, not a
  // translation of anything he wrote.
  security_post_alarm_installation: 'Post Panel Installation',
  // Renamed from 'CCTV' (12896840378). Jaron marked it up on 2026-08-25.
  // Safe to rename outright, unlike the rest of that ticket: this key is
  // rendered as a plain section title, never as a photo row, and no customer
  // has a single photo stored under it - measured across all 3,907 customers
  // that have media.
  security_cctv: 'Camera Presentation',
  cancellation_letter: 'Cancellation letter',
  signing_recording: 'Signing recordings',
  solar_proposal: 'Solar proposal',
}

// The 8 CCTV "Cam angle" slots. Rendered dynamically inside Documentation's
// Smart Home accordion (Documentation.tsx's securityCamsMediaKinds), not listed
// in categoryToMediaKinds below — kept here so any screen that needs to
// recognize a Cam angle kind doesn't have to duplicate this list.
export const camAngleKinds: schema.MediaKinds = [
  'security_cam0',
  'security_cam1',
  'security_cam2',
  'security_cam3',
  'security_cam4',
  'security_cam5',
  'security_cam6',
  'security_cam7',
]

type CategoryToMediaKinds = Readonly<Record<string, schema.MediaKinds>>
export const categoryToMediaKinds: CategoryToMediaKinds = {
  Assessment: [
    'insulation',
    'roof',
    'windows',
    'hvac_system',
    'mini_split_surroundings',
    'problem',
  ],
  // Split out of a single 'Documentation' section (Monday 12803784158). Jaron
  // crossed that whole section out for an alarm-only customer, but it mixed
  // paperwork every job needs with paperwork only a solar job needs - hiding
  // it wholesale would have left a rep unable to upload a customer's ID. His
  // answer was to separate them: "we should probably have ID picture and other
  // documents in a general section that applies for all".
  //
  // So this one is never product-gated.
  // signing_recording: the screen recordings the app uploads while the
  // customer signs (12790174732). They were saved but no screen listed them,
  // so nobody could watch one; they show here, never in the customer report.
  Documentation: ['id', 'other_documents', 'signing_recording'],
  // And these three are solar-only. Placed between Documentation and Smart
  // Home so the two paperwork sections stay together and Smart Home stays
  // last, where reps
  // already look for it. Key order here is purely visual - unlike
  // efficiencyToInputs, nothing downstream depends on it.
  Solar: ['electricity', 'solar_proposal', 'adders'],
  'Smart Home': [
    'security_camera_locations',
    // 12896840378, Jaron 2026-09-07: "Keep cameras" + "Add S4/S3/S2" - the
    // original row stays (87 photos across 30 customers already filed under
    // it), these three are new empty rows alongside it, not a migration.
    'security_camera_locations_s4',
    'security_camera_locations_s3',
    'security_camera_locations_s2',
    // 13019073213, Jaron 2026-09-13: confirmed placement is right here,
    // alongside S4/S3/S2 - same additive pattern, new empty row.
    'security_camera_locations_indoor',
    'security_doorbell_locations',
    'security_360_house_view',
    'security_attic',
    'security_router_location',
    // Jaron reopened 12739804497 on 2026-08-12 pointing here: the
    // Cancellation letter capture belongs in Smart Home, between Router
    // Location and Post Camera Installation (originally shipped under
    // Documentation as a default while waiting for his answer).
    'cancellation_letter',
    'security_post_camera_installation',
    'security_post_alarm_installation',
  ],
}

// 'security_cctv' is rendered as a plain section title (not a MediaGroup)
// directly above the Cam angle photos in Documentation.tsx — Jaron: "This
// should just be the title. It's a completely different section." (it was
// previously in categoryToMediaKinds['Smart Home'], which rendered it as its own
// empty photo-upload row, identical to Post Camera/Alarm Installation).

/**
 * Plain section headings that print ABOVE a row on the Documentation tab,
 * keyed by the row they precede. Not media kinds - they own no photos and no
 * upload controls, they only divide the list.
 *
 * Jaron marked this one on 2026-08-24 (IMG_0978): a line drawn between
 * "Cancellation letter" and "Post Camera Installation" with "Alarm Tech
 * Pictures" written at the end of it. He chased it again on 09-03 with an
 * arrow at the same gap: "This for Example, just needs a title as I mentioned
 * before." So it heads both installation rows, with the CCTV title closing
 * the section underneath. Monday 12896840378.
 *
 * A MAP rather than the string written into the JSX, which is the lesson from
 * the CCTV title on that same ticket: it was typed into Documentation.tsx
 * while `mediaToLabel` held its own copy, so renaming it meant finding both.
 * The next heading he asks for is a line of data here, not a line of markup.
 *
 * "Alarm" is deliberate and is NOT in tension with 12824335937, which renamed
 * the row below to "Post Panel Installation" on his instruction "no puede
 * aparecer que vendemos alarmas". That rule is about what the CUSTOMER reads
 * on a contract; the Documentation tab is only ever seen by reps and techs.
 * He asked for this wording after that rename had already shipped to his
 * build - it is visible in his own 09-03 screenshot.
 */
export const mediaKindToHeadingAbove: Partial<
  Record<schema.MediaKind, string>
> = {
  security_post_camera_installation: 'Alarm Tech Pictures',
}

if (env.IS_WHITE_LABEL) {
  if (env.CODENAME === 'glow') {
    const allKinds = utils.keys(mediaToLabel)
    const nonGlowKinds = allKinds.filter(
      (field) => !schema.glowMedia.includes(field),
    )
    for (const field of nonGlowKinds) {
      delete mediaToLabel[field]
    }
    for (const [category, kinds] of utils.entries(categoryToMediaKinds)) {
      ;(categoryToMediaKinds as Writable<CategoryToMediaKinds>)[category] =
        kinds.filter((k) => schema.glowMedia.includes(k))
    }
  } else if (env.CODENAME === 'nirvana') {
    const allKinds = utils.keys(mediaToLabel)
    const nonNirvanaKinds = allKinds.filter(
      (field) => !schema.nirvanaMedia.includes(field),
    )
    for (const field of nonNirvanaKinds) {
      delete mediaToLabel[field]
    }
    for (const [category, kinds] of utils.entries(categoryToMediaKinds)) {
      ;(categoryToMediaKinds as Writable<CategoryToMediaKinds>)[category] =
        kinds.filter((k) => schema.nirvanaMedia.includes(k))
    }
  } else {
    for (const mediaKind of schema.mainOnlyMedia) {
      delete mediaToLabel[mediaKind]
    }
  }
}

export const mediaToLabelSolarSurveyOld: Partial<Record<MediaKind, string>> = {
  thermal: 'Thermal camera',
  all_exterior_walls_site_survey: 'All exterior walls',
  rafters_site_survey: 'Rafters',
  all_planes_roof_site_survey: 'All planes roof',
  electrical: 'Electrical panel (do not use, old customers only)',
  other_site_survey: 'Other',
  electrical_panel_site_survey:
    'Electrical panel (do not use, old customers only)',
}
export const solarSurveyMediaKindsOld = utils.keys(mediaToLabelSolarSurveyOld)
export const mediaToLabelSolarSurveyNew: Partial<Record<MediaKind, string>> = {
  thermal: 'Thermal camera',
  all_exterior_walls_site_survey: 'All exterior walls',
  rafters_site_survey: 'Rafters',
  all_planes_roof_site_survey: 'All planes roof',
  main_service_panel_site_survey: 'Main service panel',
  eight_foot_msp_site_survey: '8 foot msp',
  utility_meter_closeup_site_survey: 'Utility meter close up',
  main_panel_label_site_survey: 'Main panel label',
  main_panel_breakers_dead_front_included_site_survey:
    'All breakers labeled on lid',
  main_panel_breakers_dead_front_removed_site_survey:
    'All breakers with lid removed',
  main_breaker_disconnect_closeup_site_survey: 'Main breaker',
  ground_system_connected_site_survey: 'Grounding (if visible)',
  sub_panels_site_survey: 'Sub-panel(s) (if any)',
  existing_solar_system_site_survey: 'Existing solar system (if any)',
  all_appliances_and_labels_site_survey: 'All appliances and labels',
  other_site_survey: 'Other',
}
export const solarSurveyMediaKindsNew = utils.keys(mediaToLabelSolarSurveyNew)

export const mediaToLabelRoofClaims: Partial<Record<MediaKind, string>> = {
  rc_front_house: 'Front of the house',
  rc_address_verification: 'Address verification',
  rc_3d_view: '3D view',
  rc_solar_panels: 'Solar',
  rc_garage: 'Garage',
  rc_wires_home: 'Wires in to home',
  rc_siding: 'Siding',
  rc_layers: 'Shingle layers',
  rc_roof_inclination: 'Roof inclination angle',
  rc_roof_overview: 'Roof overview',
  rc_drip_edge: 'Drip edge',
  rc_gutters: 'Gutters',
  // rc_ice_shield retired (Monday 13386495658, Jaron 2026-10-25: "Remove ice
  // shield"). The media kind stays in the schema so photos already stored
  // under it are untouched; it is just no longer listed or printed.
  rc_flashings: 'Flashing',
  rc_penetrations: 'Penetrations',
  rc_hail: 'Hail damage',
  rc_other_property_wind_damage: 'Property damage',
  rc_wind_damages: 'Wind damage',
  rc_length_shingle: 'Shingle length',
  rc_shingles_color: 'Shingles color',
}

export const roofClaimsMediaKinds = utils.keys(mediaToLabelRoofClaims)

export const checkAllRoofFilled = (customer: schema.Customer): boolean => {
  const isNewerCustomer = customer.createdAt > jan302025
  const roofPitchEmpty = isNewerCustomer && customer.roof_pitch === ''
  if (customer.roof_layover_or_tear === '') {
    return false
  }
  if (customer.roof_layover_or_tear === schema.RoofWorkType.Layover) {
    if (
      customer.roof_square_footage === '' ||
      roofPitchEmpty ||
      customer.roof_layers_how_many === ''
    ) {
      return false
    }
  } else if (
    customer.roof_layover_or_tear === schema.RoofWorkType.ReRoofFlatRoof
  ) {
    if (
      customer.roof_square_footage === '' ||
      roofPitchEmpty ||
      customer.roof_layers_how_many === ''
    ) {
      return false
    }
  } else if (
    customer.roof_layover_or_tear === schema.RoofWorkType.ReRoofShingles
  ) {
    if (
      customer.roof_square_footage === '' ||
      roofPitchEmpty ||
      customer.roof_layers_how_many === '' ||
      customer.roof_tear_material === ''
    ) {
      return false
    }
  } else if (
    customer.roof_layover_or_tear === schema.RoofWorkType.ReRoofWoodShake
  ) {
    if (
      customer.roof_square_footage === '' ||
      roofPitchEmpty ||
      customer.roof_layers_how_many === '' ||
      customer.roof_tear_material === ''
    ) {
      return false
    }
  } else if (customer.roof_layover_or_tear === schema.RoofWorkType.TileRepl) {
    if (customer.roof_square_footage === '' || roofPitchEmpty) {
      return false
    }
  } else if (
    customer.roof_layover_or_tear === schema.RoofWorkType.UnderlaymentRepl
  ) {
    if (customer.roof_square_footage === '' || roofPitchEmpty) {
      return false
    }
  } else {
    console.error('Roof work type un-accounted for in checkAllRoofFilled()')
  }
  return true
}

export type Guideline = utils.r<{
  type: 'error' | 'info' | 'warning'
  text: string
}>

const roofFillAllFieldsGuideline: Guideline = {
  text: 'Fill all fields',
  type: 'warning',
}

export const getFieldGuideline = (
  customer: schema.Customer,
  field: schema.CustomerField,
): Guideline | null => {
  const installer = customer?.main_panel_upgrade_installation_company
  const noInstaller = installer === 'To Be Decided' || !installer

  if (field === 'air_conditioner_new_tons') {
    if (
      customer.air_conditioner_working === 'yes' &&
      customer.air_conditioner_unit_type.startsWith('split_') &&
      customer.air_conditioner_furnance === 'no'
    ) {
      if (!customer.air_conditioner_current_tons) {
        return {
          text: 'Select current AC tons',
          type: 'warning',
        }
      }
      return {
        text: 'Match preferred if no furnance',
        type: 'info',
      }
    }

    if (customer.attic_square_footage) {
      return {
        type: 'info',
        text: `Recommended tons = ${Math.round(
          utils.parseFloating(customer.attic_square_footage) / 400,
        )}`,
      }
    } else {
      return {
        type: 'warning',
        text: 'Enter attic square footage to get recommended tons',
      }
    }
  }
  if (
    field === 'roof_layers_how_many' &&
    customer.roof_layover_or_tear === 'Layover' &&
    utils.parseInt(customer.roof_layers_how_many) > 2
  ) {
    return {
      type: 'warning',
      text: 'For more than 2 layers you need to do a shingles re roof',
    }
  }
  if (field === 'roof_pitch') {
    if (customer.roof_layover_or_tear === schema.RoofWorkType.ReRoofFlatRoof) {
      if (utils.parseInt(customer.roof_pitch) > 1) {
        return {
          text: 'Not a flat roof?',
          type: 'error',
        }
      }
      return null
    }
    return {
      text: 'Will incur a fee if higher than 7',
      type: 'info',
    }
  }
  if (field === 'battery_size') {
    if (noInstaller) {
      return {
        text: 'Installer not selected',
        type: 'warning',
      }
    }
    if (customer?.main_panel_upgrade_installation_company === 'EcoManagement') {
      return {
        type: 'info',
        text: 'Battery size should be bigger than the system. Promo only applies to utility companies: SCE/PG&E/SDG&E',
      }
    }
    return {
      type: 'info',
      text: 'Battery size should be bigger than the system',
    }
  }
  if (field === 'battery_cost') {
    const batteryTypeSelected = !!customer.battery_type

    if (batteryTypeSelected && noInstaller) {
      return { text: 'Installer not selected', type: 'warning' }
    }
  }
  if (field === 'battery_type') {
    if (noInstaller) {
      return { text: 'Installer not selected', type: 'warning' }
    }
  }
  if (field === 'derate_cost') {
    if (noInstaller) {
      return { text: 'Installer not selected', type: 'warning' }
    }
  }
  if (field === 'main_panel_upgrade_cost') {
    if (noInstaller) {
      return { text: 'Installer not selected', type: 'warning' }
    }
  }
  if (field === 'main_panel_upgrade_installation_company') {
    if (!customer.products.includes('Solar')) {
      return { text: 'Turn on Solar in Customer Info', type: 'warning' }
    }
  }
  if (field === 'main_panel_upgrade_needed_or_requested') {
    if (noInstaller) {
      return { text: 'Installer not selected', type: 'warning' }
    }
  }
  if (field === 'roof_cost') {
    if (!checkAllRoofFilled(customer)) {
      return roofFillAllFieldsGuideline
    }
  }
  if (field === 'roof_commission_add_up') {
    if (utils.parseFloating(customer.roof_commission_add_up) > 3000) {
      return { text: 'Must be 3000 or lower', type: 'error' }
    }
  }
  if (field === 'small_system_cost') {
    if (noInstaller) {
      return { text: 'Installer not selected', type: 'warning' }
    }
  }

  return null
}

export const disableInputs: FieldToTrait<boolean> = {
  cash_cost: true,
  createdAt: true,
  date: true,
  sort_key: true,
}

/**
 * If an efficiency is disabled, the disabled should cascade down to all
 * non-notes inputs
 */
export const shouldDisableInput = (
  field: schema.CustomerField,
  customer: schema.Customer,
): boolean => {
  if (efficiencyToInputs[field] && customer.payment_is_applied) {
    return true
  }

  if (field === 'air_conditioner_new_tons') {
    return (
      customer.air_conditioner_working === 'yes' &&
      customer.air_conditioner_unit_type.startsWith('split_') &&
      customer.air_conditioner_furnance === 'no'
    )
  }
  if (field === 'security_alarm_payout') {
    return customer.payment_is_applied
  }
  if (field === 'cash_cost' || field === 'cash_customer_cost') {
    return true
  }
  if (field === 'homeStoryNumber') {
    return customer.payment_is_applied
  }
  if (field === 'main_panel_upgrade_installation_company') {
    return !customer.products.includes('Solar')
  }
  if (
    field === 'main_panel_upgrade_needed_or_requested' &&
    !customer.main_panel_upgrade_installation_company
  ) {
    return true
  }

  if (field.endsWith('notes') && !field.endsWith('extra_notes')) {
    const efficiency = fieldToEfficiency(field)
    return !!efficiencyToCalculator[efficiency!]
  }
  return Boolean(disableInputs[field])
}

export const shouldEnableInput = (
  field: schema.CustomerField,
  customer: schema.Customer,
): boolean => !shouldDisableInput(field, customer)

export const parseCashbackOptions = (options: string): utils.Opts => {
  try {
    const _options = JSON.parse(options) as unknown as utils.Opts

    return _options
  } catch (e) {
    return []
  }
}

export const getTotalCashback = (options: utils.Opts, customer: Customer) => {
  let total = 0

  if (options.length) {
    options.forEach((key) => {
      if (customer[key.value as keyof Customer]) {
        total += utils.parseFloating(
          customer[key.value as keyof Customer] as string,
        )
      }
    })
  }
  return total
}

export const fieldToEfficiency = (
  field: schema.CustomerField,
): schema.CustomerField | undefined => {
  /*
   * Longest suffix first, and cut by length rather than `replace`.
   *
   * `'roof_customer_cost'.endsWith('_cost')` is true, so with `_cost` tested
   * first the `_customer_cost` branch below could never run, and
   * `.replace('_cost', '')` left `roof_customer` - a key that belongs to
   * nothing. `_extra_notes` was eaten by `_notes` the same way and came back as
   * `roof_extra`. `costs.ts` puts this result straight into the costs slice,
   * which only has keys for real efficiencies.
   *
   * `slice` instead of `replace` because `replace` takes the FIRST occurrence,
   * not the suffix that was just matched.
   */
  for (const suffix of ['_customer_cost', '_extra_notes', '_cost', '_notes'])
    if (field.endsWith(suffix))
      return field.slice(0, -suffix.length) as schema.CustomerField
  const [efficiencyField] =
    utils
      .entries(efficiencyToInputs)
      .find(([_, subFields]) =>
        [subFields].flat(1).some((v) => v?.includes(field)),
      ) || []

  return efficiencyField
}

export const isSubField = (field: keyof Customer) => {
  let isSubfield = false
  efficiencyKeys.forEach((key) => {
    if (field?.startsWith(key) && field !== key && !field.endsWith('notes')) {
      isSubfield = true
    }
  })

  return isSubfield
}

export const fieldCanBeEmpty = (field: schema.CustomerField) => {
  if (isSubField(field)) {
    return true
  }

  // TODO: Later to be handled by the new schema validators
  return ['customerPhoneAlt', 'ssn'].includes(field)
}

export const fieldShouldBeFilled = (field: schema.CustomerField): boolean =>
  !fieldCanBeEmpty(field)

export const isLargeEfficiency = (field: keyof Customer) => {
  if (
    field === 'air_conditioner' ||
    field === 'attic_insulation' ||
    field === 'new_windows' ||
    field === 'roof'
  ) {
    return true
  }

  return false
}

//#region MPUCalculator

const MAX_PANELS = 42

interface Inverter {
  readonly capacity: number
  readonly name: string
  readonly numOfPanels: readonly [lowerBound: number, upperBound: number]
}

const availableInverters: readonly Inverter[] = [
  {
    capacity: 1.21,
    name: 'Enphase IQ8+ Microinverters.',
    numOfPanels: [1, 1],
  },
  {
    capacity: 2.42,
    name: '2 x Enphase IQ8+ Microinverters.',
    numOfPanels: [2, 2],
  },
  {
    capacity: 3.63,
    name: '3 x Enphase IQ8+ Microinverters.',
    numOfPanels: [3, 3],
  },
  {
    capacity: 4.84,
    name: '4 x Enphase IQ8+ Microinverters.',
    numOfPanels: [4, 4],
  },
  {
    capacity: 6.05,
    name: '5 x Enphase IQ8+ Microinverters.',
    numOfPanels: [5, 5],
  },
  {
    capacity: 7.26,
    name: '6 x Enphase IQ8+ Microinverters.',
    numOfPanels: [6, 6],
  },
  {
    capacity: 8.47,
    name: '7 x Enphase IQ8+ Microinverters.',
    numOfPanels: [7, 7],
  },
  {
    capacity: 16,
    name: 'Solaredge Inv-3800',
    numOfPanels: [8, 12],
  },
  {
    capacity: 21,
    name: 'Solaredge Inv-5000',
    numOfPanels: [13, 16],
  },
  {
    capacity: 25,
    name: 'Solaredge Inv-6000',
    numOfPanels: [17, 20],
  },
  {
    capacity: 32,
    name: 'Solaredge Inv-7600',
    numOfPanels: [21, 25],
  },
  {
    capacity: 40,
    name: 'Solaredge Inv-10000',
    numOfPanels: [26, 33],
  },
  {
    capacity: 47.5,
    name: 'Solaredge Inv-11400',
    numOfPanels: [34, 38],
  },
  {
    capacity: 50,
    name: 'Solaredge Inv-6000 + Solaredge Inv-6000',
    numOfPanels: [39, 40],
  },
  {
    capacity: 53,
    name: 'Solaredge Inv-7600 + Solaredge Inv 5000',
    numOfPanels: [41, 42],
  },
]

const reverseInverters = availableInverters.slice().reverse()

interface MPU {
  readonly capacity: number
  readonly name: string
}

const availableMPUs: readonly MPU[] = [
  {
    capacity: 225,
    name: '225amp MPU',
  },
]

const [maxCapInverter] = (availableMPUs as [Inverter])
  .slice()
  .sort((a, b) => a.capacity - b.capacity)

export const { capacity: MAX_MPU_RATING } = maxCapInverter!

export interface MPUCalculatorResult {
  readonly maxPanelsAsIs: number | null
  readonly maxPanelsWithDerate: number | null
  readonly inverterAsIs: Inverter | null
  readonly inverterWithDerate: Inverter | null
}

const VALID_DERATES = [100, 175, 200]

export const mpuCalculator = (
  busRating: number,
  mainBreakerRating: number,
): MPUCalculatorResult => {
  const res: Writable<MPUCalculatorResult> = {
    maxPanelsAsIs: null,
    maxPanelsWithDerate: null,
    inverterAsIs: null,
    inverterWithDerate: null,
  }

  // Information!G11 = numOfPanels

  // Information!F15 = chosenInverter according to numOfPanels
  // Information!K15 = chosenInverter.capacity * 1.25

  for (const inverter of reverseInverters) {
    const {
      capacity,
      numOfPanels: [, upperBound],
    } = inverter
    /**
     * IF (B2*1.2)>B3+(Information!C48*1.25):
     *   CONCATENATE("Maximum number of Solar Panels possible to install without the need of Derate or MPU: ",Information!A48),
     * else:
     *   IF (B2*1.2)>B3+(Information!C46*1.25):
     *      CONCATENATE("Maximum number of Solar Panels possible to install without the need of Derate or MPU: ",Information!A46)
     *   else:
     *     IF (B2*1.2)>B3+(Information!C44*1.25):
     *       CONCATENATE("Maximum number of Solar Panels possible to install without the need of Derate or MPU: ",Information!A44)
     *     else:
     *       IF (B2*1.2)>B3+(Information!C39*1.25):
     *         CONCATENATE("Maximum number of Solar Panels possible to install without the need of Derate or MPU: ",Information!A39)
     *       else:
     *         IF (B2*1.2)>B3+(Information!C31*1.25):
     *           CONCATENATE("Maximum number of Solar Panels possible to install without the need of Derate or MPU: ",Information!A31)
     *         else:
     *           IF (B2*1.2)>B3+(Information!C26*1.25):
     *             CONCATENATE("Maximum number of Solar Panels possible to install without the need of Derate or MPU: ",Information!A26)
     *           else:
     *             IF (B2*1.2)>B3+(Information!C21*1.25):
     *               CONCATENATE("Maximum number of Solar Panels possible to install without the need of Derate or MPU: ",Information!A21)
     *             else:
     *               IF (B2*1.2)>B3+(Information!C18*1.25):
     *                 CONCATENATE("Maximum number of Solar Panels possible to install without the need of Derate or MPU: ",Information!A18),"No possible to install Solar Panels without the need of Derate or MPU"))))))))
     */
    if (busRating * 1.2 > mainBreakerRating + capacity * 1.25) {
      res.maxPanelsAsIs = upperBound
      res.inverterAsIs = inverter
      break
    }
  }

  if (!res.maxPanelsAsIs || !res.inverterAsIs) {
    return res
  }

  for (const numOfPanels of range(
    MAX_PANELS - res.maxPanelsAsIs,
    res.maxPanelsAsIs + 1,
  )) {
    /**
     * IF (B2*1.2)>B3+Information!K15:
     *   "Derate is not need"
     * else IF (B2*1.2)>B3-25+Information!K15:
     *   IF B3-25=200:
     *     CONCATENATE("Derate size should be: ",B3-25)
     *   else IF B3-25=175:
     *     CONCATENATE("Derate size should be: ",B3-25,)
     *   else IF B3-25=100:
     *     CONCATENATE("Derate size should be: ",B3-25,)
     *   else:
     *    "Derate is not possible"
     * else:
     *   "Derate is not possible"
     */
    const inverterViaPanels = availableInverters.find(
      ({ numOfPanels: [lowerBound, upperBound] }) =>
        isBetween({
          lowerBound,
          upperBound,
          value: numOfPanels,
        }),
    )
    if (!inverterViaPanels) {
      return res
    }
    if (
      busRating * 1.2 >
      mainBreakerRating + inverterViaPanels.capacity * 1.25
    ) {
      // Derate not needed
      if (numOfPanels !== res.maxPanelsAsIs) {
        console.error('Assertion failed: numOfPanels === res.maxPanelsAsIs')
      }
      continue
    }

    if (
      busRating * 1.2 >
      mainBreakerRating - 25 + inverterViaPanels.capacity * 1.25
    ) {
      /**
       * IF B3-25=200:
       *     CONCATENATE("Derate size should be: ",B3-25)
       *   else IF B3-25=175:
       *     CONCATENATE("Derate size should be: ",B3-25,)
       *   else IF B3-25=100:
       *     CONCATENATE("Derate size should be: ",B3-25,)
       *   else:
       *    "Derate is not possible"
       */
      if (VALID_DERATES.includes(mainBreakerRating - 25)) {
        res.maxPanelsWithDerate = numOfPanels
        const inverterViaPanels = availableInverters.find(
          ({ numOfPanels: [lowerBound, upperBound] }) =>
            isBetween({
              lowerBound,
              upperBound,
              value: numOfPanels,
            }),
        )
        if (!inverterViaPanels) {
          console.error(
            `Found panels with derate but not Inverter for those panels?`,
          )
          continue
        }
        res.inverterWithDerate = inverterViaPanels
      }
    }
  }

  return res
}

// TODO: Move to react utils
export function useMpuCalculator() {
  const [mpuBusRating, setMpuBusRating] = React.useState('')
  const [mpuMainBreakerRating, setMPUMainBreakerRating] = React.useState('')

  const mpuCalculatorResult = React.useMemo((): MPUCalculatorResult | null => {
    // Was `[mpuBusRating, mpuBusRating]` — the same field twice, so the Main
    // Breaker Rating was never checked at all. A rep who filled the Bus
    // Rating and left the other one empty got a calculated answer built on a
    // main breaker rating of 0, presented exactly like a real one.
    if ([mpuBusRating, mpuMainBreakerRating].every(stringIsInt)) {
      // const parsedNumOfSolarPanels = parseInt(numOfSolarPanels, 10)
      const parsedMPUBusRating = utils.parseFloating(mpuBusRating)
      const parsedMPUMainBreakerRating =
        utils.parseFloating(mpuMainBreakerRating)

      return mpuCalculator(parsedMPUBusRating, parsedMPUMainBreakerRating)
    }

    return null
  }, [mpuBusRating, mpuMainBreakerRating])

  return {
    mpuBusRating,
    mpuCalculatorResult,
    mpuMainBreakerRating,
    setMpuBusRating,
    setMPUMainBreakerRating,
  }
}

//#endregion MPUCalculator

export const invoiceEfficiencyOpts: utils.DeepReadonly<
  Record<string, string[] | Record<string, string[]>>
> = {
  artificial_turf: [
    'Evergreen Collection Waterproof Solid Grass 1x1 Indoor/Outdoor Artificial Grass Tile, 1 ft. x 1 ft',
  ],
  attic_insulation: ['Attic Insulation'],
  duct_seal: ['Duct Seal'],
  efficient_boiler: [
    'Rinnai M120SN Condensing Gas Boiler, Natural Gas: Materials+Labor',
    'Aspen Firetube 126,000 BTU Combi High Efficiency Wall Hung Condensing Gas Fired Boiler: Materials+Labor',
  ],
  heat_pump: [
    'Goodman 5 Ton 15.5 SEER Heat Pump GSZC180601, Coil CHPF4860D6, 120,000 BTU 97% AFUE Horizontal Gas furnance GMVM971205DN',
    '56,000 BTU 4.6 Ton 17.5 SEER Ducted Central Split Air Conditioner Heat Pump System',
  ],
  hvac: [
    '2 T HVAC System Installation: Miscellaneous + Labor',
    '2.5 T HVAC System Installation: Miscellaneous + Labor',
    '3 T HVAC System Installation: Miscellaneous + Labor',
    '3.5 T HVAC System Installation: Miscellaneous + Labor',
    '4 T HVAC System Installation: Miscellaneous + Labor',
    '5 T HVAC System Installation: Miscellaneous + Labor',
  ],
  mini_split: [
    '1T HVAC mini split: Miscellaneous+Labor',
    '2T HVAC mini split: Miscellaneous+Labor',
  ],
  radiant_floor: ['WarmlyYours Tempzone Electric Radiant Floor  '],
  roof: {
    roof_layover: [
      'Install 30-year Shingles according to manufactures specifications\nSeal penetrations',
    ],
    shingles_tear_off: [
      'Tear off the existing roof.\nInstall one-layer of synthetic underlayment.\nInstall new 2x2 drep edge metal.\nInstall 30-year Shingles according to manufactures specifications.\nSeal penetrations.\n',
    ],
  },
  thermostat: [
    'Ecobee3 Lite Programmable Smart Thermostat - Energy Star Certified',
    'Nest Learning Thermostat - Smart Wi-Fi Thermostat Stainless Steel',
  ],
  whole_house_fans: [
    'Whole House Fan/Ventilation',
    'QA-Deluxe 6500 Includes Plug & Play Wireless On/Off Remote Control | Energy Efficient Whole House Fan | R-5 Insulated Damper',
  ],
  windows: ['Sliding Windows', 'Sliding Windows: Low-E Energy Efficient.'],
  windows_film: [
    'KESPEN Window Privacy Film One Way Daytime Privacy Static Cling Sun Blocking Anti UV Reflective Window Tint for Home and Office, Black-Silver',
  ],
}

/**
 * At this point this is just the state for the company.
 */
export const companyToLabel: utils.ReadonlyRecord<string> = (() => {
  const res: Record<string, string> = {}

  for (const [subCompanyLabel, subCompanyValue] of utils.zip(
    env.COMPANY_LABELS,
    env.COMPANY_VALUES,
  ) as readonly [string, string][]) {
    res[subCompanyValue] = subCompanyLabel
  }

  return res
})()

export const stateToSolarCompanyName: utils.ReadonlyRecord<string> = {
  california: 'California Power Solutions',
  nevada: 'Nevada Power Solutions',
}

//#region Glow
const nper = 10 // Commitment (years)
const pmt = 0 // Payment amount per period

const calcAvg = (rate: number, value: number): number => {
  const pv =
    (value * (1 - Math.pow(1 + rate, -nper))) / rate -
    (pmt * (1 - Math.pow(1 + rate, -nper))) / rate
  return Number(pv.toFixed(2))
}

const cashFlowDiscount = 0.11 // Cashflow discount
const _powerOutputMW = 0.0156

export const calculateProtocolFee = (
  pricePerWatt: number,
  hoursOfSunPerDay: number,
  escalator: number,
  powerOutputMW: number = _powerOutputMW,
): number => {
  const firstYearElectricityOldPrice = Number(
    (pricePerWatt * hoursOfSunPerDay * powerOutputMW * 365.25).toFixed(2),
  )

  const lifetimeOldElectricityValue = calcAvg(
    -escalator,
    firstYearElectricityOldPrice,
  )

  const protocolCashRequirements = calcAvg(
    cashFlowDiscount,
    lifetimeOldElectricityValue / nper,
  )

  return protocolCashRequirements
}

export const calculateInstallationCost = (
  installationCostPerWatt: number,
  powerOutputMW: number = _powerOutputMW,
): number => {
  return installationCostPerWatt * powerOutputMW * 1000000
}
//#endregion Glow

/**
 * Windows global color selectors cut-off.
 */
export const Jun4_2024 = 1717519937085
/**
 * Roof pitch cut off
 */
const jan302025 = 1738238982375
export const shouldHideInput = (
  field: schema.CustomerField,
  customer: schema.Customer,
): boolean => {
  const state = env.getCompanyState(customer.solarCompany)

  const dependency = fieldToDependency[field]
  if (dependency) {
    /* Special case */
    if (field === 'new_windows_replace_custom') {
      return customer.new_windows_replace !== 'no'
    }
    return customer[dependency] !== 'yes'
  }

  //#region efficiencies
  const { products: currProducts } = customer
  if (field === 'security_cams') {
    const shouldShow = currProducts.includes('Security') || currProducts === ''
    return !shouldShow
  }
  if (field === 'security_system') {
    const shouldShow = currProducts.includes('Security') || currProducts === ''
    return !shouldShow
  }
  if (field === 'security_tv') {
    const shouldShow = currProducts.includes('Security') || currProducts === ''
    return !shouldShow
  }
  if (field === 'security_wifi_cameras') {
    const shouldShow = currProducts.includes('Security') || currProducts === ''
    return !shouldShow
  }
  if (field === 'security_home_automation') {
    const shouldShow = currProducts.includes('Security') || currProducts === ''
    return !shouldShow
  }
  if (field === 'attic_insulation') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'duct_seal') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'inverter_swap') {
    const shouldShow = currProducts.includes('Solar') || currProducts === ''
    return !shouldShow
  }
  if (field === 'roof') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'new_windows') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'air_conditioner') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'battery') {
    const shouldShow = currProducts.includes('Solar') || currProducts !== ''
    if (!customer.solarCompany) return true
    // Nirvana in AZ uses battery_multiple
    if (state !== 'california') return true
    return !shouldShow
  }
  if (field === 'can_light_new') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'can_light') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'ceiling_fan') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'construction_work') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'home_run') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'main_panel_upgrade') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'derate') {
    const shouldShow = currProducts.includes('Solar') || currProducts === ''
    return !shouldShow
  }
  if (field === 'mini_split') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'smart_thermostat') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'attic_fan') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'pool_pump') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'duct_replacement') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'duct_repair') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'cash') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'service_and_repair') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  if (field === 'solar_tax') {
    const shouldShow = currProducts.includes('Solar') || currProducts === ''
    return !shouldShow
  }
  if (field === 'small_system') {
    const shouldShow = currProducts.includes('Solar') || currProducts === ''
    return !shouldShow
  }
  if (field === 'flat_roof_panels') {
    const shouldShow = currProducts.includes('Solar') || currProducts === ''
    return !shouldShow
  }
  if (field === 'panel_removal') {
    const shouldShow = currProducts.includes('HIL') || currProducts === ''
    return !shouldShow
  }
  //#endregion efficiencies

  if (field === 'air_conditioner_current_tons') {
    return customer.air_conditioner_working !== 'yes'
  }
  if (field === 'air_conditioner_furnance') {
    return (
      customer.air_conditioner_working === 'no' ||
      !customer.air_conditioner_unit_type.startsWith('split_')
    )
  }
  if (field === 'air_conditioner_swamp_cooler_use_remove') {
    return customer.air_conditioner_swamp_cooler_use !== 'yes'
  }
  if (field === 'air_conditioner_replace_ducts') {
    return customer.air_conditioner_working === 'no'
  }
  if (field === 'battery_size') {
    return customer.battery_type.toLowerCase() === ''
  }
  if (field === 'can_light_new_will_remove_how_many') {
    return customer.can_light_new_will_remove !== 'yes'
  }
  if (field === 'glow') {
    const shouldDisplay = env.IS_MAIN || env.CODENAME === 'glow'
    const shouldHide = !shouldDisplay
    return shouldHide
  }
  if (field === 'main_panel_upgrade_installation_company') {
    return env.CODENAME === 'nirvana'
  }
  // Hide for older customers
  if (field === 'new_windows_color') {
    return utils.normalizeTimestampToMs(customer.createdAt) < Jun4_2024
  }
  if (field === 'new_windows_sliding_glass_sqft') {
    return customer.new_windows_sliding_glass !== 'yes'
  }
  if (field === 'roof_shingles_or_tiles') {
    return env.CODENAME !== 'nirvana'
  }
  if (field === 'roof_fascia_included_square_footage') {
    return customer.roof_fascia_included !== 'yes'
  }
  if (field === 'roof_layers_how_many') {
    const shouldShow =
      customer.roof_layover_or_tear === schema.RoofWorkType.ReRoofShingles ||
      customer.roof_layover_or_tear === schema.RoofWorkType.Layover ||
      customer.roof_layover_or_tear === schema.RoofWorkType.ReRoofFlatRoof ||
      customer.roof_layover_or_tear === schema.RoofWorkType.ReRoofWoodShake

    return !shouldShow
  }
  if (field === 'roof_patio_included') {
    return customer.roof_layover_or_tear === schema.RoofWorkType.ReRoofFlatRoof
  }
  if (field === 'roof_patio_how_many') {
    return customer.roof_patio_included !== 'yes'
  }
  if (field === 'roof_pitch') {
    return customer.createdAt < jan302025
  }
  if (field === 'roof_plywood_replaced') {
    if (customer.roof_layover_or_tear === schema.RoofWorkType.TileRepl) {
      return customer.roof_plywood_under_tile === 'no'
    }
    return customer.roof_layover_or_tear === schema.RoofWorkType.Layover
  }
  if (field === 'roof_plywood_under_tile') {
    return customer.roof_layover_or_tear !== schema.RoofWorkType.TileRepl
  }
  if (field === 'roof_plywood_replaced_square') {
    return customer.roof_plywood_replaced !== 'yes'
  }
  if (field === 'roof_tear_material') {
    return (
      customer.roof_layover_or_tear !== schema.RoofWorkType.ReRoofShingles &&
      customer.roof_layover_or_tear !== schema.RoofWorkType.ReRoofWoodShake
    )
  }
  if (field === 'roof_will_add_license') {
    return state !== 'california'
  }
  if (field === 'roof_commission_add_up') {
    return state !== 'california'
  }

  return false
}

export const shouldDisplayInput = (
  field: schema.CustomerField,
  customer: schema.Customer,
): boolean => !shouldHideInput(field, customer)

export const fieldToGrow: FieldToTrait<boolean> = {
  air_conditioner_furnance: true,

  customerAddress: true,

  cash_air_conditioner: true,
  cash_artificial_turf: true,
  cash_amount: true,
  cash_irrigation_systems: true,
  cash_heat_pumps: true,
  cash_whole_house_fans_or_ventilation: true,
  cash_efficient_boilers: true,
  cash_geothermal_heat_pumps: true,
  cash_radiant_floors: true,
  cash_water_saving_fixtures: true,
  cash_solar_water_heaters: true,
  cash_on_demand_or_tankless_hot: true,
  cash_windows: true,
  cash_floor_insulation: true,
  cash_efficient_water_heaters: true,
  cash_pool_pumps: true,

  globalNotes: true,

  new_windows_color: true,
  new_windows_replace: true,
  new_windows_replace_custom: true,
  new_windows_grid: true,
  new_windows_sliding_glass: true,
  new_windows_sqft_each_window: true,

  nirvana_panels_panel_scenario: true,

  custom_fields_name: true,
  custom_two_fields_name: true,
  custom_three_fields_name: true,
  custom_four_fields_name: true,
  custom_five_fields_name: true,
  custom_six_fields_name: true,
  custom_seven_fields_name: true,
  custom_eight_fields_name: true,
  custom_nine_fields_name: true,
  custom_ten_fields_name: true,

  roof_will_add_license: true,
}

export const readableDateFormat = 'MMM Do YYYY'
export const readableDateFormatWTime = 'MMM Do YYYY HH:MM'

/**
 * US DST: 2nd Sunday of March at 2:00 a.m. standard time to the 1st Sunday of
 * November at 2:00 a.m. daylight time (federal rule since 2007), for the
 * Pacific zone Nevada and California are in: 10:00 UTC and 09:00 UTC. The
 * switch is evaluated at that exact instant. Doing it at the start of the UTC
 * day misprinted signatures inside a 9-10 hour window around each switch (a
 * 11:30pm PST Mar 7 signature printed as Mar 8th 12:30am, a 12:30am PDT Nov 1
 * one as Oct 31st 11:30pm).
 */
const isUSDaylightSaving = (atMs: number): boolean => {
  const year = new Date(atMs).getUTCFullYear()

  const march1Day = new Date(Date.UTC(year, 2, 1)).getUTCDay()
  const dstStart = Date.UTC(year, 2, 1 + ((7 - march1Day) % 7) + 7, 10)

  const november1Day = new Date(Date.UTC(year, 10, 1)).getUTCDay()
  const dstEnd = Date.UTC(year, 10, 1 + ((7 - november1Day) % 7), 9)

  return atMs >= dstStart && atMs < dstEnd
}

/**
 * Fixed UTC offset in hours for the state a contract was actually signed in.
 * Arizona never observes DST (always -7). Nevada and California do (-8
 * standard, -7 daylight). `atMs` is the signing timestamp, not now - DST
 * status is evaluated for the date being rendered, not the date it is being
 * viewed.
 */
export const contractStateUtcOffsetHours = (
  state: string,
  atMs: number,
): number => {
  if (state === 'arizona') return -7
  return isUSDaylightSaving(atMs) ? -7 : -8
}

/**
 * A contract signing date must print the date it was signed, not the date on
 * whichever machine happens to be viewing the PDF. `moment(ts).format()`
 * alone renders in the viewer's local system timezone - opening the same
 * signed contract from outside the customer's state can print the wrong
 * calendar day near local midnight. This anchors the render to the state the
 * contract was actually signed in instead.
 */
export const formatContractDate = (
  value: number | undefined,
  solarCompany: string,
  format: string = readableDateFormat,
): string => {
  if (!value) return ''
  const state = env.getCompanyState(solarCompany)
  const offsetHours = contractStateUtcOffsetHours(state, value)
  return moment(value)
    .utcOffset(offsetHours * 60)
    .format(format)
}

/**
 * The cover page "Date Signed:" day. It used to print the UTC day, so a
 * California/Nevada signature after 4-5pm Pacific (Arizona after 5pm) showed
 * the next day. Anchored to the signing state like every other contract date.
 * Normalized first because `formatContractDate` assumes milliseconds and older
 * records may hold seconds. Nirvana keeps the old render (B44).
 *
 * MM/DD/YYYY like every other date in the report and contracts; it printed
 * ISO "2026-10-27", the only date in that format (pre-deploy QA, Nov 1).
 */
export const formatCoverSignedDate = (
  value: number,
  solarCompany: string,
): string =>
  env.CODENAME === 'nirvana'
    ? utils.numberToDate(value)
    : formatContractDate(
        utils.normalizeTimestampToMs(value),
        solarCompany,
        'MM/DD/YYYY',
      )

type Calculator = (
  customer: utils.DeepReadonly<schema.Customer>,
  prices: utils.DeepReadonly<schema.SaleTag>,
  state: 'arizona' | 'california' | 'nevada',
) => [number, string]

type PriceState = 'arizona' | 'california' | 'nevada'

const NO_PRICES: readonly schema.Price[] = []

/**
 * A product's price rows for a state, or an empty list.
 *
 * Every calculator used to inline this as
 * `prices.X.california.pricesByCity[0]!.list`, and that assertion is not safe:
 * `pricesByCity` is a plain `CityPrices[]` with nothing guaranteeing an entry,
 * so a product with no California city configured took the whole costs screen
 * down. Only California is shaped per-city — Arizona and Nevada carry `list`
 * directly, which is why the branch exists at all.
 *
 * Returning an empty list rather than throwing is what the four fixes on Aug 18
 * established: a missing price is the expected condition on this catalogue, not
 * an edge case. Item Prices is edited by hand and even carries a standing "do
 * not modify Mini Split and Windows for now" banner.
 */
export const priceRowsFor = (
  product: utils.DeepReadonly<schema.SalePrices>,
  state: PriceState,
): readonly schema.Price[] =>
  (state === 'california'
    ? product.california?.pricesByCity?.[0]?.list
    : product[state]?.list) ?? NO_PRICES

/**
 * A price by label, or 0 when the catalogue has no such row.
 *
 * Matched trimmed and case-insensitively, because these labels are typed by
 * hand into the Item Prices screen and the live catalogue is not tidy. Read
 * from Firebase on 2026-08-24, three of the four rows this was extended for
 * carry a trailing space, and one differs BETWEEN STATES:
 *
 *   duct_seal   california -> 'Duct Seal '   nevada -> 'Duct Seal'
 *
 * An exact match against a literal in this file would have found the Nevada
 * row and silently returned 0 for California - a charge quietly dropping to
 * zero on a contract, which is worse than the positional read it replaces.
 *
 * Loosening the comparison can only ever find MORE rows, never fewer, so no
 * existing call site can lose a match it used to have.
 */
const priceByLabel = (rows: readonly schema.Price[], label: string): number => {
  const wanted = label.trim().toLowerCase()
  return (
    rows.find((row) => row.label.trim().toLowerCase() === wanted)?.value ?? 0
  )
}

/**
 * The value of one NAMED price row.
 *
 * These four products used to read position 0. That was right by accident:
 * each has exactly one row in the live catalogue, so the first row IS the
 * right row today. The hazard the ticket names is real all the same - the Item
 * Prices screen has arrows to reorder rows and nothing stops a second row being
 * added, and on that day the charge silently repoints at whatever sorted first.
 *
 * The labels were read from Firebase on 2026-08-24 rather than guessed; see
 * `priceByLabel` for what that turned up and why guessing would have been
 * expensive.
 *
 * A list of one is taken as-is instead of being matched by label. The row is
 * unambiguous, so there is nothing for a label to disambiguate, and matching it
 * would mean an edit to that label in Item Prices could zero out a live charge.
 * The label only decides once there is more than one row - which is exactly the
 * case this exists to get right.
 */
const namedPriceValue = (
  product: utils.DeepReadonly<schema.SalePrices>,
  state: PriceState,
  label: string,
): number => {
  const rows = priceRowsFor(product, state)

  if (rows.length <= 1) {
    return rows[0]?.value ?? 0
  }

  return priceByLabel(rows, label)
}

const defaultMiniSplitCalculator: Calculator = (
  { mini_split_tons },
  prices,
  state,
) => {
  let finalCost = 0
  const notesLines: string[] = []

  const [miniSplitTon1Price, miniSplitTon2Price] = (() => {
    const priceList = priceRowsFor(prices.mini_split, state)

    // Both lookups run here, at the top, before the rows are even parsed — so
    // a customer with no mini splits at all still reaches them. The Item
    // Prices screen carries a standing "Do not modify Mini Split and Windows
    // for now" banner, so a state missing one of these rows is the expected
    // condition, not an edge case. Priced at zero rather than throwing.
    return [
      priceList.find((cp) => cp.label === 'Mini Split System 1Ton')?.value || 0,
      priceList.find((cp) => cp.label === 'Mini Split System 2Ton')?.value || 0,
    ]
  })()

  const miniSplits = JSON.parse(mini_split_tons || '[]') as schema.MiniSplit[]

  for (const split of miniSplits.filter((ms) => ms.qty && ms.tons)) {
    if (
      // @ts-ignore
      split.tons === '1' ||
      split.tons === 'mini_split_1t' ||
      // @ts-ignore Old data
      split.tons === 1
    ) {
      finalCost += utils.parseInteger(split.qty) * miniSplitTon1Price
    }
    if (
      // @ts-ignore
      split.tons === '2' ||
      split.tons === 'mini_split_2t' ||
      // @ts-ignore Old data
      split.tons === 2
    ) {
      finalCost += utils.parseInteger(split.qty) * miniSplitTon2Price
    }
  }

  return [finalCost, notesLines.join('\n')]
}

const nirvanaMiniSplitCalculator: Calculator = (
  { mini_split_tons, roof_shingles_or_tiles },
  prices,
  state,
) => {
  let finalCost = 0
  const notesLines: string[] = []

  const priceList = priceRowsFor(prices.mini_split, state)

  const [
    mini_split_price_9kBTU,
    mini_split_price_12kBTU,
    mini_split_price_18kBTU,
    mini_split_price_24kBTU,
    wiringPrice,
    conduitPrice,
  ] = (() => {
    return [
      priceList.find((cp) => cp.label === 'Mini Split 9k BTU (650 sqft)')!
        .value,
      priceList.find((cp) => cp.label === 'Mini Split 12k BTU (750 sqft)')!
        .value,
      priceList.find((cp) => cp.label === 'Mini Split 18k BTU (1000 sqft)')!
        .value,
      priceList.find((cp) => cp.label === 'Mini Split 24k BTU (1400 sqft)')!
        .value,
      priceList.find((cp) => cp.label === 'Wiring (per ft)')!.value,
      priceList.find((cp) => cp.label === 'Conduit (per ft)')!.value,
    ]
  })()

  const miniSplits = JSON.parse(mini_split_tons || '[]') as schema.MiniSplit[]

  const filledOutRows = miniSplits.filter(
    (ms) => !!ms.distanceToCondenser && !!ms.tons,
  )

  if (filledOutRows.length === 0) {
    return [0, '']
  }

  for (const split of filledOutRows) {
    finalCost +=
      utils.parseInt(split.distanceToCondenser) *
      // 4 wires
      4 *
      // TODO: Ask Nirvana why this calc
      0.65 *
      wiringPrice

    finalCost += utils.parseInt(split.distanceToCondenser) * conduitPrice

    if (split.tons === 'mini_split_9kBTU') {
      finalCost += utils.parseInteger(split.qty) * mini_split_price_9kBTU
      notesLines.push(
        `1 x MiniSplit 9k BTU - ${split.location} - ${split.distanceToCondenser}ft to condenser`,
      )
    }
    if (split.tons === 'mini_split_12kBTU') {
      finalCost += utils.parseInteger(split.qty) * mini_split_price_12kBTU
      finalCost += notesLines.push(
        `1 x MiniSplit 12k BTU - ${split.location} - ${split.distanceToCondenser}ft to condenser`,
      )
    }
    if (split.tons === 'mini_split_18kBTU') {
      finalCost += utils.parseInteger(split.qty) * mini_split_price_18kBTU
      notesLines.push(
        `1 x MiniSplit 18k BTU - ${split.location} - ${split.distanceToCondenser}ft to condenser`,
      )
    }
    if (split.tons === 'mini_split_24kBTU') {
      finalCost += utils.parseInteger(split.qty) * mini_split_price_24kBTU
      notesLines.push(
        `1 x MiniSplit 24k BTU - ${split.location} - ${split.distanceToCondenser}ft to condenser`,
      )
    }
  }

  // const conduitLength = filledOutRows.reduce(
  //   (acc, nxt) => acc + utils.parseInt(nxt.distanceToCondenser),
  //   0,
  // )
  // notesLines.push(`${conduitLength} ft of conduit in total`)
  // notesLines.push(`${conduitLength * 4} ft of wiring in total`)

  if (roof_shingles_or_tiles === 'Tiles') {
    const tilesRoofFlatFee = priceList.find(
      (cp) => cp.label === 'Flat fee for tile roofs',
    )!.value

    finalCost += tilesRoofFlatFee

    notesLines.push('')
    notesLines.push(
      `A $${tilesRoofFlatFee} tiles roof flat fee is to be charged`,
    )
  }

  return [finalCost, notesLines.join('\n')]
}

const defaultMPUCalculator: Calculator = (
  {
    main_panel_upgrade_installation_company,
    main_panel_upgrade_needed_or_requested,
  },
  prices,
  state,
) => {
  let finalCost = 0
  const notesLines: string[] = []

  const priceList = priceRowsFor(prices.main_panel_upgrade, state)

  const mpuKey = `Main Panel Upgrade-${main_panel_upgrade_installation_company}`

  // The key is built from a field the very next line checks for emptiness, so
  // this already knew it can be unset - and with it unset the key reads
  // "Main Panel Upgrade-", matches nothing, and reading .value off the missing
  // row threw. A rep who has not picked the installer yet is the normal state
  // of a customer being filled in, not an edge case.
  const price = priceList.find((x) => x.label === mpuKey)?.value ?? 0

  if (main_panel_upgrade_installation_company) {
    if (main_panel_upgrade_needed_or_requested === 'needed') {
      notesLines.push('An upgrade is needed')
      finalCost += price
    }
    if (main_panel_upgrade_needed_or_requested === 'requested') {
      notesLines.push('An upgrade was requested by the customer')
      finalCost += price
    }
    if (main_panel_upgrade_needed_or_requested === 'relocation') {
      notesLines.push(
        "Relocation requested by customer, a site survey is needed before it's performed",
      )
    }
    notesLines.push(`Installer: ${main_panel_upgrade_installation_company}`)
  }

  return [finalCost, notesLines.join('\n')]
}

const nirvanaMPUCalculator: Calculator = (
  { main_panel_upgrade_rating },
  prices,
  state,
) => {
  if (!main_panel_upgrade_rating) {
    return [0, '']
  }

  const notesLines: string[] = [main_panel_upgrade_rating]

  if (main_panel_upgrade_rating.startsWith('600')) {
    return [0, notesLines.join('\n')]
  }

  const priceList = priceRowsFor(prices.main_panel_upgrade, state)

  const price = priceList.find(
    (p) => p.label === main_panel_upgrade_rating,
  )!.value

  return [price, notesLines.join('\n')]
}

/**
 * @deprecated Nirvana requested to disable all promo pricing, leaving here for
 * reference
 */
const PROMO_PRICING_STR = 'Promo pricing has been applied'
type GlobalCalcRes = FieldToTrait<[newPrice: number, notes: string]>
/**
 * Handles global pricing schemes, such as combo deals, applicable to Nirvana
 * only right now. Returns a map of efficiency to price/notes tuple. Any pricing
 * / notes calculated by this should not be overwritten by individual
 * calculators, this global calculator will use the individual ones as needed.
 */
export const globalCalculator = (
  customer: utils.DeepReadonly<schema.Customer>,
  prices: utils.DeepReadonly<schema.SaleTag>,
  state: 'arizona' | 'california' | 'nevada',
  efficienciesChanged: schema.CustomerFields,
): GlobalCalcRes => {
  const res: Writable<GlobalCalcRes> = {}

  const inverterChanged = efficienciesChanged.includes('inverter_swap')
  const batteryChanged = efficienciesChanged.includes('battery_multiple')
  const inverterOrBatteryChanged = inverterChanged || batteryChanged

  const [inverterCost, inverterNotes] = efficiencyToCalculator.inverter_swap?.(
    customer,
    prices,
    state,
  )!
  const [batteryCost, batteryNotes] = efficiencyToCalculator.battery_multiple?.(
    customer,
    prices,
    state,
  )!
  const inverterAndBatteryBothEnabled =
    customer.inverter_swap === 'yes' && customer.battery_multiple === 'yes'
  /**
   * If cost is zero it's because something hasn't been filled in yet.
   */
  const inverterAndBatteryFilled =
    utils.parseFloating(inverterCost) > 0 &&
    utils.parseFloating(batteryCost) > 0

  if (
    // Nirvana requested to disable all promo pricing, leaving here for
    // reference
    Math.random() > 1 &&
    inverterOrBatteryChanged &&
    inverterAndBatteryBothEnabled &&
    inverterAndBatteryFilled
  ) {
    // Some necessary data hasn't been input yet
    if (
      batteryCost === 0 ||
      batteryNotes === '' ||
      inverterCost === 0 ||
      inverterNotes === ''
    ) {
      return res
    }

    let newInverterCost = inverterCost
    let newBatteryCost = batteryCost

    const inverterPriceList = priceRowsFor(prices.inverter_swap, state)
    const inverterPriceInstallFeeRegular = inverterPriceList.find(
      (p) => p.label === 'Install fee (per inverter)',
    )!.value
    const inverterPriceInstallFeePromo = inverterPriceList.find(
      (p) => p.label === 'Install fee (per inverter) (promo)',
    )!.value

    const batteryPriceList = priceRowsFor(prices.battery_multiple, state)
    const batteryPriceInstallFeeRegular = batteryPriceList.find(
      (p) => p.label === 'Install fee (per battery)',
    )!.value
    const batteryPriceInstallFeePromo = batteryPriceList.find(
      (p) => p.label === 'Install fee (per battery) (promo)',
    )!.value

    const inverterQty = utils.parseInt(customer.inverter_swap_qty)
    const batteryQty = utils.parseInt(customer.battery_multiple_how_many)
    // Deduct all regular install prices
    newInverterCost -= inverterQty * inverterPriceInstallFeeRegular
    newBatteryCost -= batteryQty * batteryPriceInstallFeeRegular
    // Add up install promo prices
    newInverterCost += inverterQty * inverterPriceInstallFeePromo
    newBatteryCost += batteryQty * batteryPriceInstallFeePromo

    res.inverter_swap = [
      newInverterCost,
      inverterNotes + '\n' + PROMO_PRICING_STR,
    ]
    res.battery_multiple = [
      newBatteryCost,
      batteryNotes + '\n' + PROMO_PRICING_STR,
    ]
  }
  // Go back to default cost/notes
  else if (inverterChanged || batteryChanged) {
    if (customer.inverter_swap === 'yes') {
      res.inverter_swap = [inverterCost, inverterNotes]
    }
    if (customer.battery_multiple === 'yes') {
      res.battery_multiple = [batteryCost, batteryNotes]
    }
  }

  return res
}

function extractPanelRange(
  str: string,
): [lowerBound: number, upperBound: number] {
  // Install for 0-10 panels (1-4Kw)
  const latterHalf = str.slice('Install for '.length)
  const digits = latterHalf.slice(0, latterHalf.indexOf('panels') - 1)

  const [lowerBoundStr, upperBoundStr] = digits.split('-')

  console.debug({
    str,
    latterHalf,
    digits,
    '[lowerBoundStr, upperBoundStr]': [lowerBoundStr, upperBoundStr],
  })

  return [Number(lowerBoundStr), Number(upperBoundStr)]
}
export const efficiencyToCalculator: FieldToTrait<Calculator> = {
  air_conditioner(
    {
      air_conditioner_furnance,
      air_conditioner_new_tons,
      air_conditioner_replace_ducts,
      air_conditioner_swamp_cooler_use,
      air_conditioner_swamp_cooler_use_remove,
      air_conditioner_unit_type,
      air_conditioner_working,
    },
    prices,
    state,
  ) {
    let finalCost = 0
    const notesLines: string[] = []

    const priceList = priceRowsFor(prices.air_conditioner, state)

    // There's now 4 types of job:
    // 1. Full new installation
    // 2. Full replacement
    // 3. Replacement without ducts
    // 4. AC only replacement
    // We won't account for a AC-only + duct replacement as it will be rare
    const installationTypeNotes = (() => {
      if (air_conditioner_working === 'no') {
        return '(new installation)'
      }
      if (air_conditioner_unit_type.startsWith('roof')) {
        // Doesn't get offered the furnance question
        if (air_conditioner_replace_ducts === 'yes') {
          return '$$__FULL__REPLACEMENT'
        }
        return '(replacement)'
      }
      if (air_conditioner_unit_type.startsWith('split')) {
        if (
          air_conditioner_furnance === 'yes' &&
          air_conditioner_replace_ducts === 'yes'
        ) {
          return '$$__FULL__REPLACEMENT'
        }
        if (air_conditioner_furnance === 'yes') {
          return '(replacement)'
        }
        if (air_conditioner_furnance === 'no') {
          return '(ac only replacement)'
        }
        if (
          air_conditioner_replace_ducts === 'yes' &&
          air_conditioner_furnance === 'no'
        ) {
          // Unhandled scenario, fall back to ac only
          return '(ac only replacement)'
        }
      }

      throw new Error('Should be unreachable?')
    })()

    const [splitOrRoof, gasOrElectric] = air_conditioner_unit_type
      .toLowerCase()
      .split('_')

    // Notes are a title plus at most two short bullets (Jaron, 2026-09-11:
    // "condensed in to 3 lines tops" - Monday 12790297504 / 13028234765). The
    // old text was a ~40-line Scope of Work template per job type.
    const unitDesc = `${air_conditioner_new_tons}-ton ${splitOrRoof}${
      splitOrRoof === 'roof' ? '' : ' ' + gasOrElectric
    }`
    const [acTitle, acScope] = ((): readonly [string, string] => {
      if (installationTypeNotes === '(new installation)') {
        return [
          'HVAC System Installation',
          'Install a new HVAC unit with furnace and ductwork',
        ]
      }
      if (installationTypeNotes === '$$__FULL__REPLACEMENT') {
        return [
          'HVAC System Replacement',
          'Replace the HVAC unit, furnace and all existing ductwork',
        ]
      }
      if (installationTypeNotes === '(replacement)') {
        return [
          'HVAC Replacement',
          'Replace the HVAC unit and connect it to the furnace',
        ]
      }
      return ['AC Replacement', 'Replace the AC unit']
    })()
    const swampNote =
      air_conditioner_swamp_cooler_use === 'yes'
        ? air_conditioner_swamp_cooler_use_remove === 'yes'
          ? '; swamp cooler will be removed'
          : '; swamp cooler is present'
        : ''

    notesLines.push(`${acTitle} (${unitDesc})`)
    notesLines.push(`• ${acScope}`)
    notesLines.push(
      `• Includes electrical connections, testing, old equipment disposal and cleanup${swampNote}`,
    )

    if (
      air_conditioner_swamp_cooler_use === 'yes' &&
      air_conditioner_swamp_cooler_use_remove === 'yes'
    ) {
      finalCost += priceByLabel(priceList, 'Swamp Cooler Removal')
    }

    const installationTypePrice = (() => {
      // Replacing all AC furnance and ducts is the same as new install
      if (installationTypeNotes === '$$__FULL__REPLACEMENT') {
        return '(new installation)'
      }
      return installationTypeNotes
    })()

    // Four conditions have to line up in one label - tonnage, split or roof,
    // gas or electric, and installation type - so this misses easily: a
    // catalogue label worded slightly differently, a tonnage with no row, or
    // splitOrRoof/gasOrElectric still undefined because the rep has not
    // answered those questions yet. All three of those are ordinary states, and
    // any of them used to throw here rather than price at zero.
    const price: number =
      priceList.find(({ label: _label }) => {
        const label = _label.toLowerCase()

        return (
          label.includes(air_conditioner_new_tons + 't') &&
          label.includes(splitOrRoof ?? '') &&
          // Roof has no gas or electric
          label.includes(splitOrRoof === 'roof' ? '' : gasOrElectric ?? '') &&
          label.includes(installationTypePrice)
        )
      })?.value ?? 0

    finalCost += price

    if (
      installationTypePrice === '(ac only replacement)' &&
      air_conditioner_replace_ducts === 'yes'
    ) {
      const ductReplPrice: number = priceByLabel(
        priceList,
        'Duct Replacement (AC only)',
      )

      finalCost += ductReplPrice
    }

    return [finalCost, notesLines.join('\n')]
  },
  attic_fan({ attic_fan_quantity }, prices, state) {
    let finalCost = 0
    const notesLines: string[] = []

    const price = namedPriceValue(
      prices.attic_fan,
      state,
      'Turbine Ventilators',
    )

    if (attic_fan_quantity) {
      finalCost += price * utils.parseInteger(attic_fan_quantity)
      notesLines.push(`${attic_fan_quantity} attic fans will be installed`)
    }

    return [finalCost, notesLines.join('\n')]
  },
  attic_insulation(
    { attic_insulation_how_much, attic_insulation_type },
    prices,
    state,
  ) {
    if (!attic_insulation_how_much || !attic_insulation_type) {
      return [0, '']
    }
    let finalCost = 0
    const notesLines: string[] = []
    const priceList = priceRowsFor(prices.attic_insulation, state)
    const price = priceList[0]?.value || 0
    finalCost = price * utils.parseFloating(attic_insulation_how_much)
    //#region notes
    if (attic_insulation_how_much) {
      notesLines.push(`Total sq of insulation: ${attic_insulation_how_much}`)
    }
    if (attic_insulation_type) {
      notesLines.push(`Type of insulation: ${attic_insulation_type}`)
    }
    //#endregion notes
    return [finalCost, notesLines.join('\n')]
  },
  battery(
    { main_panel_upgrade_installation_company, battery_size, battery_type },
    prices,
    state,
  ) {
    let finalCost = 0
    const notesLines: string[] = []

    if (
      main_panel_upgrade_installation_company &&
      battery_size &&
      battery_type
    ) {
      const priceList = priceRowsFor(prices.battery, state)

      const batteryKey = `Battery ${battery_type}-${main_panel_upgrade_installation_company}-${battery_size}`

      // Three-part composite key built entirely from customer data, so any of
      // the type, the installer or the size not matching a catalogue row
      // returns nothing. The `|| 0` was already here, which is the author
      // saying this can come back missing — the non-null assertion in front of
      // it made that unreachable, because reading .value threw first. Same
      // defect that took the main panel upgrade calculator down when the
      // installation company had not been chosen.
      const price = priceList.find((x) => x.label === batteryKey)?.value || 0

      finalCost += price

      notesLines.push(`Installer: ${main_panel_upgrade_installation_company}`)
      notesLines.push(`Battery type: ${battery_type}`)
      notesLines.push(`Battery spec: ${battery_size}`)
    }

    return [finalCost, notesLines.join('\n')]
  },
  battery_multiple(
    {
      battery_multiple_bank_location,
      battery_multiple_customer_material,
      battery_multiple_distance_to_main_panel,
      battery_multiple_how_many,
    },
    prices,
    state,
  ) {
    const qty = utils.parseInt(battery_multiple_how_many)
    const distance = utils.parseInt(battery_multiple_distance_to_main_panel)

    if (!battery_multiple_bank_location || !qty) {
      return [0, '']
    }

    let finalCost = 0
    const notesLines: string[] = []

    const priceList = priceRowsFor(prices.battery_multiple, state)
    const priceInstallFee =
      priceList.find(
        (p) =>
          p.label ===
          (battery_multiple_customer_material === 'yes'
            ? 'Install fee (per battery) (materials provided by customer)'
            : 'Install fee (per battery)'),
      )?.value || 0
    const priceEachBattery =
      priceList.find((p) => p.label === battery_multiple_bank_location)
        ?.value || 0
    const priceWiringOutdoor =
      priceList.find((p) => p.label === 'Outdoor wiring (per ft)')?.value || 0
    const priceWiringIndoor =
      priceList.find((p) => p.label === 'Indoor wiring (per ft)')?.value || 0
    const systemToWiring: Record<string, number> = {
      'EG4 14.3 Outdoor': priceWiringOutdoor,
      'EG4 16 Kw Indoor': priceWiringIndoor,
      'Fortress Power 12k Envy': priceWiringOutdoor,
    }
    const priceWiring = systemToWiring[battery_multiple_bank_location] || 0
    const priceConduit =
      priceList.find((p) => p.label === '¾" Conduit (per ft)')?.value || 0

    finalCost += qty * priceEachBattery
    finalCost += qty * priceInstallFee
    finalCost += distance * priceConduit
    finalCost +=
      distance * // 4 wires?
      4 *
      // TODO: Ask Nirvana why this calc
      0.65 *
      priceWiring

    if (qty > 1) {
      notesLines.push(`${qty} x batteries`)
    } else {
      notesLines.push('One battery will be installed')
    }
    notesLines.push(
      `${distance} ft of ${battery_multiple_bank_location} wiring total`,
    )
    notesLines.push(`Bank location: ${battery_multiple_bank_location}s`)
    // notesLines.push(`${distance * qty} ft of ¾" conduit in total`)

    return [finalCost, notesLines.join('\n')]
  },
  can_light_new(
    {
      can_light_new_how_many,
      can_light_new_will_remove,
      can_light_new_will_remove_how_many,
      can_light_new_high_ceiling,
    },
    prices,
    state,
  ) {
    let finalCost = 0
    const notesLines: string[] = []

    const priceList = priceRowsFor(prices.can_light_new, state)

    const qty = utils.parseInt(can_light_new_how_many)
    const perLightPrice = priceList.find((p) => p.label === 'Per light')!.value
    finalCost += qty * perLightPrice
    notesLines.push(`${qty} x can light`)

    if (can_light_new_will_remove === 'yes') {
      const removalPrice = priceList.find(
        (p) => p.label === 'Removal (per light)',
      )!.value
      const removeQty = utils.parseInt(can_light_new_will_remove_how_many)
      finalCost += removeQty * removalPrice
      notesLines.push(`${removeQty} x can light removal`)
    }

    if (can_light_new_high_ceiling === 'yes') {
      const scaffoldingPrice = priceList.find(
        (p) => p.label === 'Scaffolding (ceilings above 12ft)',
      )!.value
      finalCost += scaffoldingPrice
      notesLines.push('Ceiling height above 12ft, scaffolding will be used')
    }

    const minimumCharge = priceList.find(
      (p) => p.label === 'Minimum charge',
    )!.value
    return [Math.max(minimumCharge, finalCost), notesLines.join('\n')]
  },
  can_light({ can_light_detail }, prices, state) {
    const filledOutRows = (
      JSON.parse(can_light_detail || '[]') as schema.CanLightGroup[]
    ).filter((cf) => !!cf.room && !!cf.ceilingHeight && !!cf.qty)

    if (filledOutRows.length === 0) {
      return [0, '']
    }

    const priceList = priceRowsFor(prices.can_light, state)

    const minimumCharge = priceList.find(
      (p) => p.label === 'Minimum charge',
    )!.value
    const perLightCharge = priceList.find((p) => p.label === 'Per light')!.value
    const scaffoldingCharge = priceList.find(
      (p) => p.label === 'Scaffolding (ceilings above 12ft)',
    )!.value

    let finalCost = 0
    const notesLines: string[] = []

    for (const group of filledOutRows) {
      const qty = utils.parseInt(group.qty)
      finalCost += qty * perLightCharge
      notesLines.push(
        `${qty} x can light${group.room ? ' - ' + group.room : ''}`,
      )
    }

    if (filledOutRows.some((g) => utils.parseFloating(g.ceilingHeight) > 12)) {
      finalCost += scaffoldingCharge
    }

    return [Math.max(minimumCharge, finalCost), notesLines.join('\n')]
  },
  ceiling_fan({ ceiling_fan_detail }, prices, state) {
    // Empty until the rep adds a row, and createEmptyCustomer starts every
    // string field that way - so this threw on any customer whose costs were
    // worked out before a ceiling fan existed. The four sibling calculators
    // (mini split, can lights, outlets, sliding doors) all guard it this way.
    const filledOutRows = (
      JSON.parse(ceiling_fan_detail || '[]') as schema.CeilingFan[]
    ).filter((cf) => !!cf.type)

    if (filledOutRows.length === 0) {
      return [0, '']
    }

    const priceList = priceRowsFor(prices.ceiling_fan, state)

    let finalCost = 0
    const notesLines: string[] = []

    for (const ceilingFan of filledOutRows) {
      // The type is whatever was saved on the customer, and this catalogue
      // gets renamed - "Indoor Camera" became "S4 Max", "eufyCam S3 Pro"
      // became "SolarCam S3 Pro". A customer saved before a rename still
      // holds the old label, so the lookup misses and reading .value off
      // nothing took the whole cost calculation down. Price it at zero and
      // still list the line, so the rep sees the item is there and priced
      // wrong rather than losing the screen.
      const price =
        priceList.find((p) => p.label === ceilingFan.type)?.value ?? 0
      finalCost += price
      notesLines.push(
        `1 x ${ceilingFan.type}${
          ceilingFan.notes ? ' -- ' + ceilingFan.notes : ''
        }`,
      )
    }

    return [finalCost, notesLines.join('\n')]
  },
  derate({ main_panel_upgrade_installation_company }, prices, state) {
    if (!main_panel_upgrade_installation_company) {
      return [0, '']
    }

    const price = priceByLabel(
      priceRowsFor(prices.derate, state),
      `Derate (${main_panel_upgrade_installation_company})`,
    )

    return [
      price,
      `A derate will be performed\nInstaller: ${main_panel_upgrade_installation_company}`,
    ]
  },
  duct_seal(_, prices, state) {
    const price = namedPriceValue(prices.duct_seal, state, 'Duct Seal')

    return [price, 'Duct seal will be applied.']
  },
  flat_roof_panels({ flat_roof_panels_how_many }, prices, state) {
    let finalCost = 0
    const notesLines: string[] = []
    const price = namedPriceValue(
      prices.flat_roof_panels,
      state,
      'Flat Roof Panels',
    )

    if (flat_roof_panels_how_many) {
      finalCost += price * utils.parseInteger(flat_roof_panels_how_many)
    }

    return [finalCost, notesLines.join('\n')]
  },
  home_run({ home_run_ft }, prices, state) {
    if (!home_run_ft) {
      return [0, '']
    }

    const priceList = priceRowsFor(prices.home_run, state)

    const basePrice = priceList.find(
      (p) => p.label === 'New home runs (up to 20ft)',
    )!.value

    let finalCost = 0

    finalCost += basePrice

    const ft = utils.parseFloating(home_run_ft)

    if (ft > 20) {
      const additionalFt = ft - 20
      const pricePerFt = priceList.find(
        (p) => p.label === 'Per feet after 21ft',
      )!.value
      finalCost += pricePerFt * additionalFt
    }

    return [finalCost, `${ft} feet of new home runs`]
  },
  inverter_swap(
    {
      inverter_swap_customer_material,
      inverter_swap_distance_to_mpu,
      inverter_swap_qty,
      inverter_swap_size,
      main_panel_location,
    },
    prices,
    state,
  ) {
    let finalCost = 0
    const notesLines: string[] = []

    if (
      !inverter_swap_distance_to_mpu ||
      !inverter_swap_size ||
      !inverter_swap_qty ||
      !main_panel_location
    ) {
      return [finalCost, '']
    }

    const priceList = priceRowsFor(prices.inverter_swap, state)

    const systemPrices = {
      'EG4 6000 XP': priceList.find((cp) => cp.label === 'EG4 6000 XP')?.value,
      'EG4 12000 XP': priceList.find((cp) => cp.label === 'EG4 12000 XP')
        ?.value,
      'EG4 12KPV': priceList.find((cp) => cp.label === 'EG4 12KPV')?.value,
      'EG4 18KPV': priceList.find((cp) => cp.label === 'EG4 18KPV')?.value,
      'EG4 flex boss 18': priceList.find(
        (cp) => cp.label === 'EG4 flex boss 18',
      )?.value,
      'EG4 flex boss 21': priceList.find(
        (cp) => cp.label === 'EG4 flex boss 21',
      )?.value,
      'Fortress Power 12k': priceList.find(
        (cp) => cp.label === 'Fortress Power 12k',
      )?.value,
      'Envy Duo 21': priceList.find((cp) => cp.label === 'Envy Duo 21')?.value,
    } as utils.DictB<number>

    const wiring_price_3_fourths_price = priceList.find(
      (cp) => cp.label === '¾" Wiring (per ft)',
    )
    const wiring_price_1_inch_price = priceList.find(
      (cp) => cp.label === '1" Wiring (per ft)',
    )
    const wiring_price_1_1_fourth_price = priceList.find(
      (cp) => cp.label === '1-¼" Wiring (per ft)',
    )
    const systemToWiring = {
      'EG4 6000 XP': wiring_price_3_fourths_price,
      'EG4 12000 XP': wiring_price_1_inch_price,
      'EG4 12KPV': wiring_price_1_inch_price,
      'EG4 18KPV': wiring_price_1_1_fourth_price,
      'EG4 flex boss 18': wiring_price_1_1_fourth_price,
      'EG4 flex boss 21': wiring_price_1_1_fourth_price,
      'Fortress Power 12k': wiring_price_1_1_fourth_price,
    } as utils.DictB<schema.Price>
    const wiringPrice = systemToWiring[inverter_swap_size]

    const conduit_price_3_fourths_price = priceList.find(
      (cp) => cp.label === '¾" Conduit (per ft)',
    )
    const conduit_price_1_inch_price = priceList.find(
      (cp) => cp.label === '1" Conduit (per ft)',
    )
    const conduit_price_1_1_fourth_price = priceList.find(
      (cp) => cp.label === '1-¼" Conduit (per ft)',
    )
    // TODO: How to have this in the DB
    const systemToConduit = {
      'EG4 6000 XP': conduit_price_3_fourths_price,
      'EG4 12000 XP': conduit_price_1_inch_price,
      'EG4 12KPV': conduit_price_1_inch_price,
      'EG4 18KPV': conduit_price_1_1_fourth_price,
      'EG4 flex boss 18': conduit_price_1_1_fourth_price,
      'EG4 flex boss 21': conduit_price_1_1_fourth_price,
      'Fortress Power 12k': conduit_price_1_1_fourth_price,
      'Envy Duo 21': conduit_price_1_1_fourth_price,
    } as utils.DictB<schema.Price>

    const conduitPrice = systemToConduit[inverter_swap_size]

    finalCost += systemPrices[inverter_swap_size] || 0
    finalCost +=
      utils.parseInt(inverter_swap_distance_to_mpu) * (conduitPrice?.value || 0)
    finalCost +=
      utils.parseInt(inverter_swap_distance_to_mpu) *
      // 4 wires
      4 *
      // TODO: Ask Nirvana why this calc
      0.65 *
      (wiringPrice?.value || 0)

    const installPriceRegular = priceList.find(
      (cp) => cp.label === 'Install fee (per inverter)',
    )?.value

    const installPriceMaterialsProvided = priceList.find(
      (cp) =>
        cp.label ===
        'Install fee (per inverter) (materials provided by customer)',
    )?.value

    finalCost +=
      (inverter_swap_customer_material === 'yes'
        ? installPriceMaterialsProvided
        : installPriceRegular) || 0

    notesLines.push(`System size: ${inverter_swap_size}`)
    notesLines.push(`Main panel location: ${main_panel_location}`)
    notesLines.push(
      `${inverter_swap_distance_to_mpu} ft of conduit (Distance to Main Panel)`,
    )
    notesLines.push(
      `${utils.parseInt(inverter_swap_distance_to_mpu) * 4} ft of wiring`,
    )
    notesLines.push(
      `Conduit width: ${conduitPrice?.label.replace(' Conduit (per ft)', '')}`,
    )
    notesLines.push(
      inverter_swap_customer_material === 'yes'
        ? 'Customer will provide materials'
        : 'Nirvana energy will provide materials',
    )

    finalCost *= inverter_swap_qty

    return [finalCost, notesLines.join('\n')]
  },
  main_panel_upgrade:
    env.CODENAME === 'nirvana' ? nirvanaMPUCalculator : defaultMPUCalculator,
  mini_split:
    env.CODENAME === 'nirvana'
      ? nirvanaMiniSplitCalculator
      : defaultMiniSplitCalculator,
  new_windows(
    {
      createdAt,
      new_windows_color,
      new_windows_grid,
      new_windows_sliding_glass,
      new_windows_sliding_glass_sqft,
      new_windows_sqft_each_window,
    },
    prices,
    state,
  ) {
    let finalCost = 0
    const notesLines: string[] = []
    const pricePerSqft = priceByLabel(
      priceRowsFor(prices.new_windows, state),
      'Per Sqft',
    )
    // @ts-ignore Special Case
    const priceColors = prices.new_windows.priceColors

    const windowGroups: readonly schema.WindowGroup[] = JSON.parse(
      new_windows_sqft_each_window || '[]',
    )
    if (!utils.isArray(windowGroups)) {
      throw new TypeError('new_windows_sqft_each_window not an array')
    }
    for (const obj of windowGroups) {
      if (!schema.isWindowGroup(obj)) {
        throw new TypeError(`Object inside sqftWindow[] not a WindowGroup`)
      }
    }
    const filledWindowGroups = windowGroups.filter(
      (sw) => !!sw.size && !!sw.window,
    )
    let totalSqft = 0
    let totalWindows = 0
    let amtColor = 0
    for (const windowGroup of filledWindowGroups) {
      const qty = utils.parseInteger(windowGroup.window)
      const size = utils.parseFloating(windowGroup.size)

      totalSqft += qty * size
      totalWindows += qty

      if (createdAt > Jun4_2024) {
        // Newer customer, has a global color selector
        const colorPrice = priceColors[new_windows_color]
        if (colorPrice) amtColor += colorPrice * qty
      } else {
        // Older customer, has an individual color selector for each window
        // group
        const colorPrice = priceColors[windowGroup.color]
        if (colorPrice) amtColor += colorPrice * qty
      }
    }
    finalCost += utils.parseFloating(pricePerSqft) * totalSqft + amtColor
    notesLines.push('Scope of work:')
    notesLines.push('')
    notesLines.push(
      'Remove and replace existing windows with new energy-efficient models. The windows will be properly installed and sealed. Old materials will be disposed of, and the worksite will be cleaned post-installation.',
    )
    notesLines.push('')
    notesLines.push('Type of windows: Low E, doble panel windows')
    notesLines.push('Material: Vinyl')
    if (new_windows_color) {
      notesLines.push(`Color: ${new_windows_color}`)
    }
    if (totalWindows > 0) {
      notesLines.push(`Total number of windows: ${totalWindows}`)
    }
    if (totalSqft > 0) {
      notesLines.push(`Total measure of windows in sqft: ${totalSqft}`)
    }

    const slidingNotes: string[] = []
    if (new_windows_sliding_glass === 'yes') {
      // Priced at zero when a row is missing rather than throwing. Two blocks
      // above, this same function reads 'Per Sqft' as
      // `[state]?.list?.find(...)?.value || 0` — the author already knew both
      // the state entry and the label can be absent. These three lookups
      // dropped every one of those guards, and the Item Prices screen carries
      // a standing "Do not modify Mini Split and Windows for now" banner, so
      // these lists are known to be incomplete. A missing price must not take
      // down whatever screen asked for the total.
      const slidingPrice = (label: string): number => {
        const list =
          state === 'california'
            ? prices.new_windows.california?.pricesByCity?.[0]?.list
            : prices.new_windows[state]?.list

        return list?.find((cp) => cp.label === label)?.value || 0
      }

      const slidingDoorPrices = {
        'Sliding Doors (5 ft)': slidingPrice('Sliding Doors (5 ft)'),
        'Sliding Doors (6 ft)': slidingPrice('Sliding Doors (6 ft)'),
        'Sliding Doors (8 ft)': slidingPrice('Sliding Doors (8 ft)'),
      }
      const slidingDoors = JSON.parse(
        new_windows_sliding_glass_sqft || '[]',
      ) as schema.SlidingGlassDoorGroup[]
      const filledSlidingDoors = slidingDoors.filter(
        (sd) => !!sd.size && !!sd.total,
      )
      for (const slidingDoor of filledSlidingDoors) {
        // A size that is not one of the three keys indexes to undefined, and
        // `finalCost += undefined * n` poisons the running total with NaN for
        // every later line too — which reaches contracts as the literal
        // string "NaN", the same failure applyLoanToSecurity produced.
        const price =
          slidingDoorPrices[
            slidingDoor.size as keyof typeof slidingDoorPrices
          ] ?? 0
        finalCost += price * utils.parseInteger(slidingDoor.total)
      }

      const totalSliding = filledSlidingDoors.reduce(
        (count, g) => count + utils.parseInt(g.total),
        0,
      )
      if (totalSliding) {
        notesLines.push(`Total number of sliding glass doors: ${totalSliding}`)
      }

      slidingNotes.push(
        ...filledSlidingDoors.map(
          (g) =>
            `${g.total} Sliding glass ${
              g.total > 1 ? 'doors' : 'door'
            } - Size of the sliding ${g.total > 1 ? 'doors' : 'door'} ${
              schema.slidingToSize[g.size as keyof typeof schema.slidingToSize]
            } ft`,
        ),
      )
    }

    notesLines.push(`Grids included: ${new_windows_grid}`)

    notesLines.push('Windows Details')

    notesLines.push(
      ...filledWindowGroups.map(
        (g) =>
          `${g.window} ${g.window > 1 ? 'windows' : 'window'} - Size ${
            g.size
          } sqft${
            createdAt < Jun4_2024 && g.color
              ? ` - Color ${g.color.toLowerCase()}`
              : ''
          }`,
      ),
    )

    notesLines.push(...slidingNotes)

    return [finalCost, notesLines.join('\n')]
  },
  nirvana_panels(
    {
      nirvana_panels_distance_to_main_panel,
      nirvana_panels_how_many,
      nirvana_panels_panel_scenario,
    },
    prices,
    state,
  ) {
    if (
      !nirvana_panels_distance_to_main_panel ||
      !nirvana_panels_how_many ||
      !nirvana_panels_panel_scenario
    ) {
      return [0, '']
    }

    const qty = utils.parseInt(nirvana_panels_how_many)

    const priceList = priceRowsFor(prices.nirvana_panels, state)

    let finalCost = 0
    const notesLines: string[] = []

    const pricePerPanel = priceList.find(
      (p) => p.label === 'Price per panel',
    )!.value
    finalCost += qty * pricePerPanel

    const priceInstallForQty = priceList.find((p) => {
      const [lowerBound, upperBound] = extractPanelRange(p.label)

      console.debug([lowerBound, upperBound])

      return qty >= lowerBound && qty <= upperBound
    })!.value
    finalCost += priceInstallForQty

    const priceConduit = priceList.find(
      (p) => p.label === '1-¼" Conduit (per ft)',
    )!.value
    const distance = utils.parseFloating(nirvana_panels_distance_to_main_panel)
    finalCost += distance * priceConduit

    const subpanelInstallPrice = priceList.find(
      (p) => p.label === 'Subpanel install',
    )!.value
    const gridTiedPermits = priceList.find(
      (p) => p.label === 'Grid-tied permits',
    )!.value

    notesLines.push(`${qty} panels will be installed`)

    if (nirvana_panels_panel_scenario === 'Grid-tied') {
      finalCost += gridTiedPermits
      notesLines.push('Grid permits will be pulled')
      finalCost += subpanelInstallPrice
      notesLines.push('Sub panel will be installed')
    }
    if (nirvana_panels_panel_scenario === 'Off-grid') {
      finalCost += subpanelInstallPrice
      notesLines.push('Sub panel will be installed')
    }

    return [finalCost, notesLines.join('\n')]
  },
  outlet({ outlet_detail }, prices, state) {
    const filledOutRows = (
      JSON.parse(outlet_detail || '[]') as schema.OutletGroup[]
    ).filter(
      (og) =>
        !!og.room &&
        utils.parseInt(og.qty) > 0 &&
        (JSON.parse(og.relocationFeet) as string[]).every((s) => !!s),
    )

    if (filledOutRows.length === 0) {
      return [0, '']
    }

    const priceList = priceRowsFor(prices.outlet, state)
    const priceMinimumCharge = priceList.find(
      (p) => p.label === 'Minimum charge',
    )!.value
    const priceFirstOutlet = priceList.find(
      (p) => p.label === 'First outlet (up to 10ft relocation)',
    )!.value
    const priceAdditionalOutlet = priceList.find(
      (p) => p.label === 'Additional outlet (up to 10ft relocation)',
    )!.value
    const priceAdditionalRelFt = priceList.find(
      (p) => p.label === 'Additional relocation ft (after 10 feet)',
    )!.value
    const priceMaxChargeOneOutlet = priceList.find(
      (p) => p.label === 'Max charge for 1 outlet',
    )!.value

    let finalCost = 0
    const notesLines: string[] = []

    for (const {
      qty: _qty,
      relocationFeet: _relocationFeet,
      room,
    } of filledOutRows) {
      const qty = utils.parseInt(_qty)
      const relocationFeet = JSON.parse(_relocationFeet) as utils.strings

      notesLines.push(`(${room}) ${qty} x outlets`)
      notesLines.push(`Relocation feet each:`)
      relocationFeet.forEach((ft, i) => {
        notesLines.push(`  (${String.fromCharCode(65 + i)}): ${ft}ft`)
      })

      let thisRoomCost = 0

      // Already filtered above to have qty >= 1
      thisRoomCost += priceFirstOutlet

      thisRoomCost +=
        priceAdditionalRelFt *
        Math.max(0, utils.parseFloating(relocationFeet[0]) - 10)

      for (const i of utils.range(qty - 1, 1)) {
        const ft = utils.parseFloating(relocationFeet[i])
        thisRoomCost += priceAdditionalOutlet
        thisRoomCost += Math.max(0, ft - 10) * priceAdditionalRelFt
      }

      if (qty === 1) {
        thisRoomCost = Math.min(thisRoomCost, priceMaxChargeOneOutlet)
      }
      finalCost += thisRoomCost
    }

    return [Math.max(finalCost, priceMinimumCharge), notesLines.join('\n')]
  },
  panel_removal({ panel_removal_how_many }, prices, state) {
    let finalCost = 0
    const notesLines: string[] = []

    // An empty or absent list for a state made `list[0]` undefined, and
    // reading .value off it threw — taking down whatever screen asked for the
    // total, not just this one number.
    //
    // KNOWN REMAINING HAZARD, deliberately not "fixed" here: this reads the
    // row by POSITION while every sibling calculator matches on `label`. The
    // Item Prices admin screen has up and down arrows to reorder rows, so
    // reordering silently repoints this at a different product's price — no
    // crash, just a wrong number on the customer's contract. Switching to a
    // label lookup needs the real label confirmed against the live price
    // list first; guessing it would zero this charge out silently, which is
    // worse than the hazard it replaces.
    const price = (() => {
      const list =
        state === 'california'
          ? prices.panel_removal.california?.pricesByCity?.[0]?.list
          : prices.panel_removal[state]?.list

      return list?.[0]?.value || 0
    })()

    if (panel_removal_how_many) {
      finalCost += price * utils.parseInteger(panel_removal_how_many)
      notesLines.push(`${panel_removal_how_many} panels will be removed`)
    }

    return [finalCost, notesLines.join('\n')]
  },
  protection({ protection_which }, prices, state) {
    if (!protection_which) {
      return [0, '']
    }

    const notesLines: string[] = []
    notesLines.push(`Type of surge protection: ${protection_which}`)

    const priceList = priceRowsFor(prices.protection, state)

    const price = priceList.find((p) => p.label === protection_which)!.value

    return [price, notesLines.join('\n')]
  },
  roof(customer, prices, state) {
    if (!checkAllRoofFilled(customer)) {
      return [0, '']
    }
    const {
      roof_fascia_included_square_footage,
      roof_fascia_included,
      roof_layers_how_many,
      roof_layover_or_tear,
      roof_tear_material,
      roof_patio_how_many,
      roof_patio_included,
      roof_pitch,
      roof_plywood_replaced_square,
      roof_plywood_replaced,
      roof_plywood_under_tile,
      roof_square_footage,
      roof_will_add_license,
      roof_commission_add_up,
    } = customer

    const priceList = priceRowsFor(prices.roof, state)
    let finalCost = 0
    // Facts for the single detail bullet; see the summary built at the end.
    const factLines: string[] = []

    if (roof_pitch) {
      factLines.push(`pitch ${roof_pitch}`)
    }
    if (roof_tear_material) {
      factLines.push(`${roof_tear_material}-year material`)
    }

    if (roof_square_footage && roof_layover_or_tear) {
      // Only the work type actually chosen gets priced.
      //
      // All six prices were read here eagerly, before the map below picked one
      // - so a Layover job still forced the tile, underlayment, flat-roof and
      // woodshake lookups, and died on any of those rows being absent from the
      // catalogue for work nobody asked about. The `|| 0` the original author
      // left under the shingle and woodshake variants says a missing row was
      // always meant to price at zero; the `!` above them made that
      // unreachable.
      //
      // layoverPrice's two branches were byte-identical - priceList is already
      // resolved for the state by the time it is read, so the california check
      // decided nothing.
      //
      // The duration allowlists are kept verbatim rather than built from
      // `roof_tear_material`. Shingles are priced at 20/30/50 and woodshake at
      // 30 only (the picker enforces that with roofMaterialDurationsOnly30),
      // and woodshake's 20- and 50-year rows are both commented out in the
      // catalogue AND spelled without "per sq" - so deriving the label would
      // start charging for rows that used to price at zero. The outer `|| -1`
      // stays for the same reason: it is reachable only on a stale duration,
      // and moving it would move a number on screen, which is not what this
      // fix is for.
      const pricePerSq =
        (() => {
          switch (roof_layover_or_tear) {
            case schema.RoofWorkType.Layover:
              return priceByLabel(priceList, schema.RoofWorkType.Layover)
            case schema.RoofWorkType.TileRepl:
              return priceByLabel(priceList, 'Tile (per sq)')
            case schema.RoofWorkType.UnderlaymentRepl:
              return priceByLabel(
                priceList,
                'Underlayment Replacement (per sq)',
              )
            case schema.RoofWorkType.ReRoofFlatRoof:
              return priceByLabel(priceList, 'Re Roof (Flat roof) per sq')
            case schema.RoofWorkType.ReRoofShingles:
              return ['20', '30', '50'].includes(roof_tear_material)
                ? priceByLabel(
                    priceList,
                    `Re Roof (Shingles) (${roof_tear_material} years)`,
                  )
                : 0
            case schema.RoofWorkType.ReRoofWoodShake:
              return roof_tear_material === '30'
                ? priceByLabel(
                    priceList,
                    'Re Roof (Woodshake) per sq (30 years)',
                  )
                : 0
            default:
              return 0
          }
        })() || -1

      const roofSquares = utils.parseFloating(roof_square_footage)

      finalCost += pricePerSq * roofSquares

      if (utils.isNumber(roof_pitch) && Number(roof_pitch) >= 8) {
        const price = priceByLabel(priceList, 'High pitch roof fee (per sq)')

        finalCost += roofSquares * price
      }

      if (roof_layers_how_many) {
        if (roof_layover_or_tear === schema.RoofWorkType.ReRoofShingles) {
          const additionalLayersPrice = priceByLabel(
            priceList,
            'Tear-off cost per additional layer',
          )

          factLines.push(`${roof_layers_how_many} current layer(s)`)
          const additionalLayers = utils.parseInteger(roof_layers_how_many) - 1
          if (additionalLayers > 0) {
            finalCost += additionalLayersPrice * additionalLayers * roofSquares
            factLines.push(`${additionalLayers} additional layer(s) torn off`)
          }
        } else if (roof_layover_or_tear === schema.RoofWorkType.Layover) {
          factLines.push(`${roof_layers_how_many} layer(s) to be installed`)
        } else {
          factLines.push(`${roof_layers_how_many} current layer(s)`)
        }
      }
    }

    if (
      roof_layover_or_tear === schema.RoofWorkType.ReRoofShingles ||
      (roof_layover_or_tear === schema.RoofWorkType.TileRepl &&
        roof_plywood_under_tile === 'yes') ||
      roof_layover_or_tear === schema.RoofWorkType.UnderlaymentRepl ||
      roof_layover_or_tear === schema.RoofWorkType.ReRoofFlatRoof ||
      roof_layover_or_tear === schema.RoofWorkType.ReRoofWoodShake
    ) {
      if (roof_plywood_replaced === 'yes' && roof_plywood_replaced_square) {
        const roofPlywoodPrice = priceByLabel(
          priceList,
          'Plywood replacement per sq',
        )

        finalCost +=
          roofPlywoodPrice * utils.parseFloating(roof_plywood_replaced_square)
        factLines.push(`${roof_plywood_replaced_square} sq of plywood replaced`)
      } else {
        factLines.push('plywood replaced as needed')
      }
    }

    if (roof_fascia_included === 'yes' && roof_fascia_included_square_footage) {
      const roofFasciaPrice = priceByLabel(
        priceList,
        'Roof Fascia per linear ft',
      )

      finalCost +=
        roofFasciaPrice *
        utils.parseFloating(roof_fascia_included_square_footage)

      factLines.push(
        `fascia included (${roof_fascia_included_square_footage} sq)`,
      )
    }

    if (roof_patio_included === 'yes' && roof_patio_how_many) {
      const roofPatioPrice = priceByLabel(priceList, 'Flat roof per sq')

      finalCost += roofPatioPrice * utils.parseFloating(roof_patio_how_many)
      factLines.push(`patio included (${roof_patio_how_many} sq)`)
    }

    // Notes are a title plus at most two short bullets (Jaron, 2026-09-11:
    // "condensed in to 3 lines tops" - Monday 12790297504 / 13028234765). The
    // old text was a 40-100 line Scope of Work template per work type.
    const roofScope = ((): string => {
      switch (roof_layover_or_tear) {
        case schema.RoofWorkType.Layover:
          return 'New layer of architectural shingles over the existing roof, with drip edge, underlayment and flashings'
        case schema.RoofWorkType.ReRoofShingles:
          return 'Tear off to the sheathing and install new architectural shingles, drip edge, underlayment and flashings'
        case schema.RoofWorkType.TileRepl:
          return 'Remove old tiles, replace underlayment and install new tiles with sealed edges and flashing'
        case schema.RoofWorkType.UnderlaymentRepl:
          return 'Lift tiles, replace underlayment and reinstall the tiles, replacing any broken ones'
        case schema.RoofWorkType.ReRoofFlatRoof:
          return 'Remove the flat roof down to the decking and install a new torch-down system with flashings and drainage'
        case schema.RoofWorkType.ReRoofWoodShake:
          return 'Remove the wood shake roof and install a new plywood deck, underlayment, shingles and flashings'
        default:
          return ''
      }
    })()
    if (
      roof_layover_or_tear === schema.RoofWorkType.Layover ||
      roof_layover_or_tear === schema.RoofWorkType.ReRoofShingles
    ) {
      factLines.push('permits included')
    }
    const roofWork =
      roof_layover_or_tear === schema.RoofWorkType.ReRoofShingles
        ? 'Re Roof (Shingles)'
        : roof_layover_or_tear
    const notesLines: string[] = []
    if (roofWork) {
      notesLines.push(
        `Roof: ${roofWork}${
          roof_square_footage ? ` (${roof_square_footage} sq)` : ''
        }`,
      )
    }
    if (roofScope) notesLines.push(`• ${roofScope}`)
    if (factLines.length) {
      notesLines.push(
        `• ${factLines.join(', ').replace(/^./, (ch) => ch.toUpperCase())}`,
      )
    }

    if (roof_will_add_license === 'no') {
      finalCost -= 1000
    }
    if (roof_commission_add_up) {
      finalCost += utils.parseFloating(roof_commission_add_up)
    }

    return [finalCost, notesLines.join('\n')]
  },
  security_cams({ security_cams_detail }, prices, state) {
    let finalCost = 0
    const notesLines: string[] = []

    const priceList = priceRowsFor(prices.security_cams, state)

    const camGroups = JSON.parse(
      security_cams_detail || '[]',
    ) as schema.CamGroup[]

    const filledOutRows = camGroups.filter((cg) => !!cg.qty && !!cg.type)

    if (filledOutRows.length === 0) {
      return [0, '']
    }

    for (const row of filledOutRows) {
      // Destructuring directly in the for-of head, repeated across these
      // sibling calculators, hits a real @react-native/babel-preset bug: its
      // loop-closure extraction generates a reference to an undeclared
      // `_notesN` temp instead of the actual destructured value, throwing
      // "_notesN is not defined" on ANY row with a non-empty notes field -
      // confirmed against the actual compiled output, independent of
      // whether the product type is priced or not. Destructuring inside the
      // loop body instead (like ceiling_fan's property-access style above)
      // avoids whatever triggers it.
      const { notes, qty, type } = row
      // Catalog rows get renamed/removed over time (update-security-cams-products.ts
      // deleted 'Home Base 3', 'Solar S4 Camera', 'NVR WiFi Module (Eufy)' and
      // renamed 'Bullet Cam E40' -> 'Bullet Cam E40 (PoE)') - customers saved
      // before a change still hold the old label, and .find() returns
      // undefined for it. Pricing the unknown item at zero keeps the total
      // showing instead of taking the whole cost screen down.
      const price = priceList.find((p) => p.label === type)?.value ?? 0

      finalCost += qty * price

      notesLines.push(`${qty} x ${type}` + (notes ? ` (${notes})` : ''))
    }

    // Blank line between products (not '\n') so Cost.tsx can render a visual
    // divider between each product's own notes block, instead of one
    // product's last bullet running straight into the next product's header
    // (Jaron: "show a separation between these").
    return [finalCost, notesLines.join('\n\n')]
  },
  security_wifi_cameras({ security_wifi_cameras_detail }, prices, state) {
    let finalCost = 0
    const notesLines: string[] = []

    const priceList = priceRowsFor(prices.security_wifi_cameras, state)

    const camGroups = JSON.parse(
      security_wifi_cameras_detail || '[]',
    ) as schema.CamGroup[]

    const filledOutRows = camGroups.filter((cg) => !!cg.qty && !!cg.type)

    if (filledOutRows.length === 0) {
      return [0, '']
    }

    for (const row of filledOutRows) {
      // Keep destructuring in the body, NOT the for-of head - see the note
      // on security_cams above. Moving it back breaks this calculator on any
      // row that has notes.
      const { notes, qty, type } = row
      // Same rename/removal exposure as security_cams above - e.g. 'Solar S4'
      // was removed from the catalog, and a customer saved before that still
      // holds it.
      const price = priceList.find((p) => p.label === type)?.value ?? 0

      finalCost += qty * price

      notesLines.push(`${qty} x ${type}` + (notes ? ` (${notes})` : ''))
    }

    return [finalCost, notesLines.join('\n\n')]
  },
  security_home_automation({ security_home_automation_detail }, prices, state) {
    let finalCost = 0
    const notesLines: string[] = []

    const priceList = priceRowsFor(prices.security_home_automation, state)

    const camGroups = JSON.parse(
      security_home_automation_detail || '[]',
    ) as schema.CamGroup[]

    const filledOutRows = camGroups.filter((cg) => !!cg.qty && !!cg.type)

    if (filledOutRows.length === 0) {
      return [0, '']
    }

    for (const row of filledOutRows) {
      // Keep destructuring in the body, NOT the for-of head - see the note
      // on security_cams above. Moving it back breaks this calculator on any
      // row that has notes.
      const { notes, qty, type } = row
      // Same exposure - e.g. 'Smart Lock C220' saved on an older customer,
      // since renamed or removed from the catalog.
      const price = priceList.find((p) => p.label === type)?.value ?? 0

      finalCost += qty * price

      notesLines.push(`${qty} x ${type}` + (notes ? ` (${notes})` : ''))
    }

    return [finalCost, notesLines.join('\n\n')]
  },
  security_system({ security_system_detail }, prices, state) {
    let finalCost = 0
    const notesLines: string[] = []

    const priceList = priceRowsFor(prices.security_system, state)

    const secSysGroups = JSON.parse(
      security_system_detail || '[]',
    ) as schema.SecSysGroup[]

    const filledOutRows = secSysGroups.filter((ssg) => !!ssg.qty && !!ssg.type)

    if (filledOutRows.length === 0) {
      return [0, '']
    }

    for (const row of filledOutRows) {
      // Keep destructuring in the body, NOT the for-of head - see the note
      // on security_cams above. Moving it back breaks this calculator on any
      // row that has notes.
      const { notes, qty, type } = row
      // Same exposure as the other security_* calculators above.
      const price = priceList.find((p) => p.label === type)?.value ?? 0

      finalCost += qty * price

      notesLines.push(`${qty} x ${type}` + (notes ? ` (${notes})` : ''))
    }

    return [finalCost, notesLines.join('\n\n')]
  },
  security_tv({ security_tv_detail }, prices, state) {
    let finalCost = 0
    const notesLines: string[] = []

    const priceList = priceRowsFor(prices.security_tv, state)

    const securityTvRows = JSON.parse(
      security_tv_detail || '[]',
    ) as schema.SecurityTVs

    const filledOutRows = securityTvRows.filter((ssg) => !!ssg.type)

    if (filledOutRows.length === 0) {
      return [0, '']
    }

    for (const { type } of filledOutRows) {
      // Same exposure as the other security_* calculators above.
      const price = priceList.find((p) => p.label.startsWith(type))?.value ?? 0

      finalCost += price

      notesLines.push(`1 x ${type}`)
    }

    // Blank line between products, like the other four security calculators.
    // A single '\n' left this the only category whose products ran together,
    // and it also made `alarmSafeNotesBlock` treat the whole TV list as ONE
    // block: only the first line got header treatment, so any later product
    // whose name hit a banned word was deleted along with its quantity.
    return [finalCost, notesLines.join('\n\n')]
  },
  service_and_repair({ service_and_repair_hours }, prices, state) {
    if (!service_and_repair_hours) {
      return [0, '']
    }

    const priceList = priceRowsFor(prices.service_and_repair, state)

    const firstHourPrice = priceList.find(
      (p) => p.label === 'Minimum charge (first hour)',
    )!.value

    let finalCost = firstHourPrice

    const additionalHours = utils.parseFloating(service_and_repair_hours) - 1

    const pricePerHour = priceList.find(
      (p) => p.label === 'Charge per hour after the first',
    )!.value
    finalCost += pricePerHour * additionalHours

    return [
      finalCost,
      `${service_and_repair_hours} hour${
        additionalHours ? 's' : ''
      } of service and repairs work`,
    ]
  },
  small_system(
    {
      main_panel_upgrade_installation_company,
      small_system_size,
      small_system_extra_batteries_qty,
      small_system_extra_batteries_will,
    },
    prices,
    state,
  ) {
    if (!main_panel_upgrade_installation_company || !small_system_size) {
      return [0, '']
    }

    let finalCost = 0
    const notesLines: string[] = []

    const priceList = priceRowsFor(prices.small_system, state)

    // Only the size the customer chose is looked up. All three used to be read
    // before this line picked one, so a 6kw job died on a missing 12kw row for
    // a system nobody had quoted - the same eager-lookup shape as the roof work
    // types.
    //
    // And an unrecognised size made `price` undefined, so `finalCost +=
    // undefined` poisoned the entire total into NaN, which reaches the contract
    // as the literal string "NaN". Identical defect to 12826620765 on the
    // sliding-door sizes. Zero is the honest answer for a size we have no price
    // for; NaN silently destroys every other line on the quote.
    const price = ['6kw', '12kw', '18kw'].includes(small_system_size)
      ? priceByLabel(priceList, `${small_system_size} system`)
      : 0

    finalCost += price

    notesLines.push(`System size: ${small_system_size}`)

    notesLines.push(`Includes 6 panels and one 14.3kw battery`)

    if (
      small_system_extra_batteries_will === 'yes' &&
      small_system_extra_batteries_qty
    ) {
      const extraBatteriesParsed = utils.parseInt(
        small_system_extra_batteries_qty,
      )
      const additionalBatteryPrice = priceByLabel(
        priceList,
        'Additional battery (ea)',
      )

      finalCost += extraBatteriesParsed * additionalBatteryPrice

      if (extraBatteriesParsed === 1) {
        notesLines.push(`1 extra battery will be installed`)
      } else if (extraBatteriesParsed > 1) {
        notesLines.push(
          `${small_system_extra_batteries_qty} extra batteries will be installed`,
        )
      }
    }

    const miscPrice = priceByLabel(priceList, 'Misc (wiring, etc.)')

    finalCost += miscPrice

    return [finalCost, notesLines.join('\n')]
  },
  smart_thermostat({ smart_thermostat_how_many }, prices, state) {
    let finalCost = 0
    const notesLines: string[] = []

    const price = namedPriceValue(prices.smart_thermostat, state, 'Thermostat')

    if (smart_thermostat_how_many) {
      finalCost += price * utils.parseInteger(smart_thermostat_how_many)
      notesLines.push(
        `${smart_thermostat_how_many} smart thermostats will be installed`,
      )
    }

    return [finalCost, notesLines.join('\n')]
  },
}

export const addUpWindows = (customer: Customer): number | string => {
  try {
    const windows = JSON.parse(
      customer.new_windows_sqft_each_window || '[]',
    ) as schema.WindowGroup[]

    return windows
      .filter((w) => !!w.size)
      .reduce((acc, curr) => acc + utils.parseInt(curr.window), 0)
  } catch (e) {
    const msg = `Error adding up windows: ${utils.processErr(e)}`
    utils.log(e)
    return msg
  }
}

export const addUpSlidingDoors = (customer: Customer): number | string => {
  try {
    if (customer.new_windows_sliding_glass === 'no') {
      return 0
    }
    const slidingDoors = JSON.parse(
      (customer.new_windows_sliding_glass_sqft as string) || '[]',
    ) as schema.SlidingGlassDoorGroup[]

    return slidingDoors
      .filter((s) => !!s.size)
      .reduce((acc, curr) => acc + utils.parseInt(curr.total), 0)
  } catch (e) {
    const msg = `Error adding up sliding doors: ${utils.processErr(e)}`
    utils.log(e)
    return msg
  }
}

/**
 * In order to apply the loan to all efficiencies, we need to know which ones
 * are actually filled, sometimes this is done via the calculator (if it returns
 * 0 the efficiency isn't fully filled in) but this is a hack.
 */
const efficiencyToIsFilled: FieldToTrait<
  (customer: schema.Customer) => boolean
> = {
  air_conditioner({
    air_conditioner_current_tons,
    air_conditioner_furnance,
    air_conditioner_new_tons,
    air_conditioner_replace_ducts,
    air_conditioner_swamp_cooler_use_remove,
    air_conditioner_swamp_cooler_use,
    air_conditioner_unit_type,
    air_conditioner_working,
  }) {
    if (!air_conditioner_working) {
      return false
    }
    if (air_conditioner_working === 'yes') {
      if (!air_conditioner_current_tons) {
        return false
      }
      if (!air_conditioner_replace_ducts) {
        return false
      }
    }
    if (!air_conditioner_swamp_cooler_use) {
      return false
    }
    if (air_conditioner_swamp_cooler_use === 'yes') {
      if (!air_conditioner_swamp_cooler_use_remove) {
        return false
      }
    }
    if (!air_conditioner_new_tons) {
      return false
    }
    if (!air_conditioner_unit_type) {
      return false
    }
    if (air_conditioner_unit_type.startsWith('split_')) {
      if (!air_conditioner_furnance) {
        return false
      }
    }
    return true
  },
  attic_fan({ attic_fan_quantity }) {
    if (!attic_fan_quantity) {
      return false
    }
    return true
  },
  attic_insulation({ attic_insulation_type, attic_insulation_how_much }) {
    if (!attic_insulation_type) {
      return false
    }
    if (!attic_insulation_how_much) {
      return false
    }
    return true
  },
  battery_multiple({
    battery_multiple_bank_location,
    battery_multiple_distance_to_main_panel,
    battery_multiple_how_many,
    battery_multiple_customer_material,
  }) {
    if (!battery_multiple_bank_location) {
      return false
    }
    if (!battery_multiple_distance_to_main_panel) {
      return false
    }
    if (!battery_multiple_how_many) {
      return false
    }
    if (!battery_multiple_customer_material) {
      return false
    }
    return true
  },
  battery({
    battery_type,
    battery_size,
    main_panel_upgrade_installation_company,
  }) {
    if (!battery_type) {
      return false
    }
    if (!battery_size) {
      return false
    }
    if (!main_panel_upgrade_installation_company) {
      return false
    }
    return true
  },
  can_light_new({
    can_light_new_how_many,
    can_light_new_will_remove,
    can_light_new_will_remove_how_many,
  }) {
    if (!can_light_new_how_many) {
      return false
    }
    if (
      can_light_new_will_remove === 'yes' &&
      !can_light_new_will_remove_how_many
    ) {
      return false
    }
    return true
  },
  can_light({ can_light_detail }) {
    if (!can_light_detail) {
      return false
    }
    return true
  },
  // cash() {},
  ceiling_fan({ ceiling_fan_detail }) {
    if (!ceiling_fan_detail) {
      return false
    }
    return true
  },
  construction_work() {
    return true
  },
  derate({ main_panel_upgrade_installation_company }) {
    if (!main_panel_upgrade_installation_company) {
      return false
    }
    return true
  },
  duct_repair({ duct_repair_quantity }) {
    if (!duct_repair_quantity) {
      return false
    }
    return true
  },
  duct_replacement({ duct_replacement_qty }) {
    if (!duct_replacement_qty) {
      return false
    }
    return true
  },
  duct_seal() {
    return true
  },
  flat_roof_panels({ flat_roof_panels_how_many }) {
    if (!flat_roof_panels_how_many) {
      return false
    }
    return true
  },
  home_run({ home_run_ft }) {
    if (!home_run_ft) {
      return false
    }
    return true
  },
  inverter_swap({
    inverter_swap_qty,
    inverter_swap_size,
    inverter_swap_distance_to_mpu,
    inverter_swap_customer_material,
    main_panel_location,
  }) {
    if (!inverter_swap_qty) {
      return false
    }
    if (!inverter_swap_size) {
      return false
    }
    if (!inverter_swap_distance_to_mpu) {
      return false
    }
    if (!inverter_swap_customer_material) {
      return false
    }
    if (!main_panel_location) {
      return false
    }
    return true
  },
  main_panel_upgrade({
    main_panel_upgrade_rating,
    main_panel_upgrade_installation_company,
    main_panel_upgrade_needed_or_requested,
  }) {
    if (env.CODENAME === 'nirvana') {
      if (!main_panel_upgrade_rating) {
        return false
      }
      return true
    }
    if (!main_panel_upgrade_installation_company) {
      return false
    }
    if (!main_panel_upgrade_needed_or_requested) {
      return false
    }
    return true
  },
  mini_split({ mini_split_tons, roof_shingles_or_tiles }) {
    if (env.CODENAME === 'nirvana') {
      if (!roof_shingles_or_tiles) {
        return false
      }
      if (!mini_split_tons) {
        return false
      }
      return true
    }
    if (!mini_split_tons) {
      return false
    }
    return true
  },
  new_windows({
    new_windows_sqft_each_window,
    new_windows_color,
    new_windows_grid,
    new_windows_sliding_glass,
    new_windows_sliding_glass_sqft,
    new_windows_replace,
  }) {
    // Doesn't take into account recent changes, won't work for older customers
    if (!new_windows_sqft_each_window) {
      return false
    }
    if (!new_windows_color) {
      return false
    }
    if (!new_windows_grid) {
      return false
    }
    if (!new_windows_sliding_glass) {
      return false
    }
    if (new_windows_sliding_glass === 'yes') {
      if (!new_windows_sliding_glass_sqft) {
        return false
      }
    }
    if (!new_windows_replace) {
      return false
    }
    return true
  },
  nirvana_panels({
    nirvana_panels_panel_scenario,
    nirvana_panels_distance_to_main_panel,
    nirvana_panels_how_many,
  }) {
    if (!nirvana_panels_panel_scenario) {
      return false
    }
    if (!nirvana_panels_distance_to_main_panel) {
      return false
    }
    if (!nirvana_panels_how_many) {
      return false
    }
    return true
  },
  outlet({ outlet_detail }) {
    if (!outlet_detail) {
      return false
    }
    return true
  },
  panel_removal({ panel_removal_how_many }) {
    if (!panel_removal_how_many) {
      return false
    }
    return true
  },
  pool_pump() {
    return true
  },
  protection({ protection_which }) {
    if (!protection_which) {
      return false
    }
    return true
  },
  roof({
    roof_square_footage,
    roof_pitch,
    roof_layover_or_tear,
    roof_layers_how_many,
    roof_tear_material,
    roof_fascia_included,
    roof_fascia_included_square_footage,
    roof_patio_included,
    roof_patio_how_many,
    roof_plywood_under_tile,
    roof_plywood_replaced,
    roof_plywood_replaced_square,
    roof_will_add_license,
  }) {
    // Doesn't take into account recent changes, won't work for older customers
    if (!roof_square_footage) {
      return false
    }
    if (!roof_pitch) {
      return false
    }
    if (!roof_layover_or_tear) {
      return false
    }
    if (
      roof_layover_or_tear === schema.RoofWorkType.ReRoofShingles ||
      roof_layover_or_tear === schema.RoofWorkType.ReRoofFlatRoof ||
      roof_layover_or_tear === schema.RoofWorkType.ReRoofWoodShake ||
      roof_layover_or_tear === schema.RoofWorkType.Layover
    ) {
      if (!roof_layers_how_many) {
        return false
      }
      if (
        roof_layover_or_tear === schema.RoofWorkType.ReRoofShingles ||
        roof_layover_or_tear === schema.RoofWorkType.ReRoofWoodShake
      ) {
        if (!roof_tear_material) {
          return false
        }
      }
    }
    if (roof_layover_or_tear === schema.RoofWorkType.TileRepl) {
      if (!roof_plywood_under_tile) {
        return false
      }
      if (roof_plywood_under_tile === 'yes') {
        if (!roof_plywood_replaced) {
          return false
        }
        if (roof_plywood_replaced === 'yes') {
          if (!roof_plywood_replaced_square) {
            return false
          }
        }
      }
    }
    if (
      roof_layover_or_tear === schema.RoofWorkType.ReRoofFlatRoof ||
      roof_layover_or_tear === schema.RoofWorkType.ReRoofShingles ||
      roof_layover_or_tear === schema.RoofWorkType.ReRoofWoodShake ||
      roof_layover_or_tear === schema.RoofWorkType.UnderlaymentRepl
    ) {
      if (!roof_plywood_replaced) {
        return false
      }
      if (roof_plywood_replaced === 'yes') {
        if (!roof_plywood_replaced_square) {
          return false
        }
      }
    }
    if (!roof_fascia_included) {
      return false
    }
    if (roof_fascia_included === 'yes') {
      if (!roof_fascia_included_square_footage) {
        return false
      }
      return true
    }
    if (!roof_patio_included) {
      return false
    }
    if (roof_patio_included === 'yes') {
      if (!roof_patio_how_many) {
        return false
      }
      return true
    }
    if (!roof_will_add_license) {
      return false
    }
    return true
  },
  security_cams({ security_cams_detail }) {
    if (!security_cams_detail) {
      return false
    }
    return true
  },
  security_wifi_cameras({ security_wifi_cameras_detail }) {
    if (!security_wifi_cameras_detail) {
      return false
    }
    return true
  },
  security_home_automation({ security_home_automation_detail }) {
    if (!security_home_automation_detail) {
      return false
    }
    return true
  },
  security_system({ security_system_detail }) {
    if (!security_system_detail) {
      return false
    }
    return true
  },
  security_tv({ security_tv_detail }) {
    if (!security_tv_detail) {
      return false
    }
    return true
  },
  service_and_repair({ service_and_repair_hours }) {
    if (!service_and_repair_hours) {
      return false
    }
    return true
  },
  small_system({
    main_panel_upgrade_installation_company,
    small_system_size,
    small_system_extra_batteries_will,
  }) {
    if (env.CODENAME === 'nirvana') {
      if (!small_system_size) {
        return false
      }
      if (!small_system_extra_batteries_will) {
        return false
      }
    } else {
      if (!main_panel_upgrade_installation_company) {
        return false
      }
    }
    return true
  },
  smart_thermostat({ smart_thermostat_how_many }) {
    if (!smart_thermostat_how_many) {
      return false
    }
    return true
  },
  custom_fields({ custom_fields_qty, custom_fields_name, custom_fields_cost }) {
    if (!custom_fields_qty) {
      return false
    }
    if (!custom_fields_name) {
      return false
    }
    if (!custom_fields_cost) {
      return false
    }
    return true
  },
  custom_two_fields({
    custom_two_fields_qty,
    custom_two_fields_name,
    custom_two_fields_cost,
  }) {
    if (!custom_two_fields_qty) {
      return false
    }
    if (!custom_two_fields_name) {
      return false
    }
    if (!custom_two_fields_cost) {
      return false
    }
    return true
  },
  custom_three_fields({
    custom_three_fields_qty,
    custom_three_fields_name,
    custom_three_fields_cost,
  }) {
    if (!custom_three_fields_qty) {
      return false
    }
    if (!custom_three_fields_name) {
      return false
    }
    if (!custom_three_fields_cost) {
      return false
    }
    return true
  },
  custom_four_fields({
    custom_four_fields_qty,
    custom_four_fields_name,
    custom_four_fields_cost,
  }) {
    if (!custom_four_fields_qty) {
      return false
    }
    if (!custom_four_fields_name) {
      return false
    }
    if (!custom_four_fields_cost) {
      return false
    }
    return true
  },
  custom_five_fields({
    custom_five_fields_qty,
    custom_five_fields_name,
    custom_five_fields_cost,
  }) {
    if (!custom_five_fields_qty) {
      return false
    }
    if (!custom_five_fields_name) {
      return false
    }
    if (!custom_five_fields_cost) {
      return false
    }
    return true
  },
  custom_six_fields({
    custom_six_fields_qty,
    custom_six_fields_name,
    custom_six_fields_cost,
  }) {
    if (!custom_six_fields_qty) {
      return false
    }
    if (!custom_six_fields_name) {
      return false
    }
    if (!custom_six_fields_cost) {
      return false
    }
    return true
  },
  custom_seven_fields({
    custom_seven_fields_qty,
    custom_seven_fields_name,
    custom_seven_fields_cost,
  }) {
    if (!custom_seven_fields_qty) {
      return false
    }
    if (!custom_seven_fields_name) {
      return false
    }
    if (!custom_seven_fields_cost) {
      return false
    }
    return true
  },
  custom_eight_fields({
    custom_eight_fields_qty,
    custom_eight_fields_name,
    custom_eight_fields_cost,
  }) {
    if (!custom_eight_fields_qty) {
      return false
    }
    if (!custom_eight_fields_name) {
      return false
    }
    if (!custom_eight_fields_cost) {
      return false
    }
    return true
  },
  custom_nine_fields({
    custom_nine_fields_qty,
    custom_nine_fields_name,
    custom_nine_fields_cost,
  }) {
    if (!custom_nine_fields_qty) {
      return false
    }
    if (!custom_nine_fields_name) {
      return false
    }
    if (!custom_nine_fields_cost) {
      return false
    }
    return true
  },
  custom_ten_fields({
    custom_ten_fields_qty,
    custom_ten_fields_name,
    custom_ten_fields_cost,
  }) {
    if (!custom_ten_fields_qty) {
      return false
    }
    if (!custom_ten_fields_name) {
      return false
    }
    if (!custom_ten_fields_cost) {
      return false
    }
    return true
  },
}

export type IContractDetailRow = utils.r<{
  description: string
  /**
   * Ideally this would be typed as required but firebase omits the property
   * when it's an empty array
   */
  notes?: string[]
  price: number
  qty: number
}>

export type TagToEfficiencies = utils.DictB<
  utils.RRecord<schema.CustomerField, true>
>

export type SharedContractNirvana = utils.r<{
  /**
   * report_nirvana_finance
   */
  amountFinance: string
  /**
   * report_nirvana_upfront
   */
  amountUpfront: string
  /**
   * report_nirvana_remaining
   */
  amountRemaining: string

  contractCompanyAddress1: string
  contractCompanyAddress2: string
  contractCompanyEmail?: string
  contractCompanyLogo: string
  contractCompanyLongName: string
  contractCompanyPhoneNumber: string
  contractCompanyRoofingLicense: string
  contractCompanyShortName: string
  contractCompanyStates: string

  customerAddress: string
  customerEmail: string
  customerName: string
  customerPhone: string
  customerPhoneAlt: string

  /**
   * The signature itself cannot be stored in a publicly accessible firebase
   * location, we'll only keep track of whether it's signed or not, the
   * signature itself will be saved into a publicly-writable but not
   * publicly-readable location, and this value here will keep track of the
   * shared contract already being signed.
   */
  customerSigned: boolean
  customerSignedDate?: number

  /**
   * homeRep
   */
  repName: string
  /**
   *
   */
  repSignatureDate?: number

  rows: IContractDetailRow[]

  state: string

  totalCostCustomer: number
}>

/**
 * Distributes a given partial loan amount into the "price" cells, the correct
 * percentage must be calculated beforehand. Rows without a quantity or type
 * cell will be discarded. Returns the resulting rows as an stringified array
 */
export const applyLoanToSecurity = (
  rows: schema.CamGroups | schema.SecSysGroups | schema.SecurityTVs,
  partialLoanAmt: number,
): string => {
  const filledRows:
    | schema.CamGroups
    | schema.SecSysGroups
    | schema.SecurityTVs = rows.filter(({ qty, type }) => qty > 0 && !!type)
  const total = filledRows.reduce((acc, { price, qty }) => acc + price * qty, 0)

  const result = filledRows.map(({ price, qty, ...rest }) => {
    const subtotal = price * qty
    // The filter above requires a quantity but says nothing about price, so a
    // group where every row is free passes it and leaves total at 0. Each
    // share was then 0/0, and that NaN went out as the literal string "NaN"
    // in contracts and financing documents. With nothing priced there is no
    // share of the loan to hand out, which is zero.
    const percentage = total === 0 ? 0 : subtotal / total
    const thisRowLoanAmt = partialLoanAmt * percentage

    return {
      ...rest,
      price: Number(thisRowLoanAmt / qty).toFixed(2),
      qty,
    }
  })

  return JSON.stringify(result)
}

export const calcLoanMonthlyPay = (
  totalLoanAmount: number,
  interestRate: number,
  loanDurationYears: number,
): number => {
  // Step 1: Convert the annual interest rate to a monthly rate
  const monthlyRate = interestRate / 100 / 12

  // Step 2: Calculate the total number of payments
  const totalPayments = loanDurationYears * 12

  // Step 3: Apply the loan amortization formula
  const monthlyPayment =
    (totalLoanAmount *
      (monthlyRate * Math.pow(1 + monthlyRate, totalPayments))) /
    (Math.pow(1 + monthlyRate, totalPayments) - 1)

  return monthlyPayment
}

export const getFilledCustomFields = (
  customerData: Customer,
): schema.CustomerFields =>
  schema.customFields
    .filter((key) => !!schema.CustomerSchema[key])
    .filter((efficiency) => {
      // Early return, matching `getFilledEfficiencies` right below. The four
      // checks used to be evaluated up front and combined with `&&` at the
      // end, so `nameFilled` ran even for a custom field the rep never turned
      // on - and on a customer document with no `_name` key it threw
      // `TypeError: Cannot read properties of undefined (reading 'length')`,
      // taking down every surface that calls this: the Job Summary and the
      // Scope of Work both do. 230 of 7,151 customers are missing at least one
      // `_name` key, 558 of those field/customer pairs are not even toggled.
      const toggled = customerData[efficiency] === 'yes'

      if (!toggled) {
        return false
      }

      const qtyFilled =
        utils.parseInt(
          customerData[(efficiency + '_qty') as schema.CustomerField],
        ) > 0
      // `|| ''` on top of the early return because one real customer has a
      // custom field toggled on with no `_name` key at all - the early return
      // alone does not cover them.
      const nameFilled =
        (
          (customerData[
            (efficiency + '_name') as schema.CustomerField
          ] as string) || ''
        ).length > 0
      const costFilled =
        !!customerData[(efficiency + '_customer_cost') as schema.CustomerField]

      return qtyFilled && nameFilled && costFilled
    })

export const getFilledEfficiencies = (
  customerData: Customer,
): schema.CustomerFields => {
  return efficiencyKeys
    .filter((key) => !!schema.CustomerSchema[key])
    .filter((efficiency) => {
      const toggled = customerData[efficiency] === 'yes'

      if (!toggled) {
        return false
      }

      const isFilled =
        efficiencyToIsFilled[efficiency as schema.CustomerField]!(customerData)

      if (rowableEfficiencies[efficiency]) {
        const rowableField = efficiencyToRowable[efficiency]
        if (!rowableField) {
          throw new ReferenceError(
            `Rowable field not found for efficiency: ${efficiency}`,
          )
        }
        const rowableDataStr = customerData[rowableField] as string
        const rowableData = JSON.parse(rowableDataStr) as schema.Rowable[]
        if (rowableData.length === 0) {
          return false
        }
      }

      return isFilled
    })
}

export const buildContractRows = (
  customerData: schema.Customer,
): readonly IContractDetailRow[] => {
  return efficiencyKeys
    .filter((k) => customerData[k] === 'yes')
    .map((k): IContractDetailRow => {
      const isCustom = k.startsWith('custom_')
      const description = isCustom
        ? (customerData[(k + '_name') as schema.CustomerField] as string)
        : getFieldLabel(k)
      const costCustomer = utils.parseFloating(
        customerData[(k + '_customer_cost') as schema.CustomerField],
      )
      const qty =
        utils.parseInt(
          (customerData[(k + '_qty') as schema.CustomerField] as
            | string
            | undefined) ||
            (customerData[(k + '_how_many') as schema.CustomerField] as
              | string
              | undefined),
        ) || -1

      return {
        description,
        notes: utils.emptyArrStr,
        price: costCustomer / (qty === -1 ? 1 : qty),
        qty,
      }
    })
}

type CalcTotalCostParams = utils.r<{
  customer: schema.Customer | null | undefined
  deductDisabled?: boolean
  /**
   * Nirvana only
   */
  twoStoryFee: number
  type: 'company' | 'customer'
}>

/**
 * The five security categories, which `nonSecurityScopeLines` must leave out.
 *
 * All five, because none of them belongs in that list: four are rendered by
 * `useSecurityScopeDetailLines` under the Smart Home heading, and
 * `security_system` - the alarm - is rendered by nothing at all. Naming either
 * group twice would charge for it twice on the page.
 *
 * CORRECTION (Luis, 2026-08-24). This comment used to claim that collapsing the
 * five into one line was "what lets security_system be charged without being
 * named". That is wrong, and it mattered: it was the reasoning behind leaving
 * the categories collapsed at all.
 *
 * TOTAL comes from `useTotalCost`, which is independent of what the page lists.
 * `useSecurityScopeDetailLines` already excludes `security_system` on its own,
 * so naming the other four individually charges the alarm without naming it
 * just as well as collapsing all five did. Luis checked it live: lines summing
 * $850 under a TOTAL of $1,545.00 with a $65.57 monthly - alarm counted, never
 * named.
 *
 * Which unblocked the half of Jaron's instruction the collapse had broken.
 * T56 quotes him and Maria on 2026-07-22: "we can only mention the types of
 * cameras we are putting in... anything with Alarms we cannot mention... so
 * everything needs to add up". Three requirements. Collapsing satisfied the
 * third and lost the first - and the first is the line Jaron circled at 2am on
 * Aug 21.
 */
export const SECURITY_SCOPE_KEYS: readonly string[] = [
  'security_cams',
  'security_system',
  'security_tv',
  'security_wifi_cameras',
  'security_home_automation',
]

export interface ScopeLine {
  readonly key: string
  readonly label: string
  readonly price: number
}

/**
 * Every OTHER piece of work on the job, as scope-of-work lines.
 *
 * The M+S Electric contract listed one line - "Smart Home" - and then a TOTAL
 * that charges for the whole job. So a customer buying cameras and an HVAC
 * system saw "Smart Home $3,000" above "TOTAL $12,000", with nothing on the
 * page accounting for the other nine thousand. That is the ticket: the scope
 * adds up only the security equipment.
 *
 * Selected with one condition only - the product is turned on. Does NOT check
 * `_show`, matching `smartHomeScopeLine` (fixed 2026-08-31, see
 * smartHomeScopeLine.test.ts). `_show === 0` does not stop `calcTotalCost`
 * from charging a turned-on product - it only skips a hidden category when
 * called with `deductDisabled`, and no contract passes it - so a hidden
 * category is not "kept off the page on purpose", it is money charged with
 * nothing naming it. Found live on James Sawkins (Caballero's, 2026-07-25):
 * `security_system_show === 0` and $2,117.98 inside the TOTAL, unnamed.
 * Same defect, same fix, applied here for ticket 12939308339.
 */
export const nonSecurityScopeLines = (
  customer: schema.Customer,
): readonly ScopeLine[] =>
  efficiencyKeys
    .filter((key) => !SECURITY_SCOPE_KEYS.includes(key))
    .filter((key) => customer[key as schema.CustomerField] === 'yes')
    .map((key) => ({
      key,
      // A custom product carries the name the rep typed in
      // `{key}_name`. Without this the contract would print the slot number -
      // "Custom 1  $2,000.00" on a document a customer signs - because
      // fieldToLabel only knows the slot, and getFieldLabel does not resolve
      // it either.
      label:
        (key.startsWith('custom_') &&
          (customer[`${key}_name` as schema.CustomerField] as string)) ||
        fieldToLabel[key as schema.CustomerField] ||
        key,
      price: utils.parseFloating(
        customer[`${key}_customer_cost` as schema.CustomerField] as string,
      ),
    }))
    .filter((line) => Number.isFinite(line.price))

export interface AirConditionerScopeDetailLine {
  readonly id: string
  readonly name: string
  readonly notes: string[]
  readonly price: number
  readonly qty: number
}

/**
 * Itemized rows for the MSE Scope of Work's air-conditioner products.
 *
 * ScopeLine.price is the saved category aggregate used by TOTAL. DetailLine
 * expects a unit price, so dividing by a positive quantity preserves the row
 * total. Only the Air Conditioner category's extra notes are included here:
 * other efficiencies may use that field for internal commission notes.
 */
export const airConditionerScopeDetailLines = (
  customer: schema.Customer,
): readonly AirConditionerScopeDetailLine[] =>
  nonSecurityScopeLines(customer)
    .filter(
      ({ key, label }) =>
        key === 'air_conditioner' ||
        (key.startsWith('custom_') && label === 'Air Conditioner'),
    )
    .map(({ key, label, price: aggregate }) => {
      const storedQty = Number(customer[`${key}_qty` as schema.CustomerField])
      const qty = Number.isFinite(storedQty) && storedQty > 0 ? storedQty : 1
      const autoNotes = customerNotes(customer, key as schema.CustomerField)
      const brandNotes =
        key === 'air_conditioner'
          ? ((customer.air_conditioner_extra_notes as string | undefined) || '')
              .split('\n')
              .filter((line) => /^\s*Brand\s*:/i.test(line))
              .map((line) => line.trim())
              .join('\n')
          : ''
      const notesText = [autoNotes, brandNotes]
        .filter((note) => note.length > 0)
        .join('\n')

      return {
        id: key,
        name: label,
        notes: notesText ? notesText.split('\n') : [],
        price: aggregate / qty,
        qty,
      }
    })

/**
 * The one line that puts the alarm's money back on the contract.
 *
 * Jaron, 2026-08-25 17:54: "Smart home section is not calculating either",
 * followed by "This could get the company in huge trouble". He was right, and
 * the cause is deliberate: `useSecurityScopeDetailLines` leaves the whole
 * `security_system` category out of the Scope of Work (T56, never show we sell
 * alarms) while `useTotalCost` still charges for it. So its money sat in the
 * TOTAL with no line accounting for it, on a document a customer signs.
 *
 * His answer at that meeting is what this implements, verbatim from SPEC: "a
 * Smart Home category line replaces Security Alarms in the Scope of Work at the
 * same price, no descriptions, so the number reconciles without naming alarms".
 *
 * So: ONE line, the category's own customer cost, and no product names — which
 * is exactly the shape `nonSecurityScopeLines` already produces. Nothing here
 * reads `security_system_detail`, so no alarm product name can reach the page
 * even if the catalogue changes.
 *
 * **2026-09-03, same day, two rounds (Ciara Wilson/Maria G):** first found
 * that `useSecurityScopeDetailLines` prints its own itemized cameras/
 * automation section under a literal "Smart Home" heading right above this
 * line (see MSElectricContract.tsx) — this line reused the exact same text
 * for a completely different number (the alarm category's own cost), so the
 * two looked like one section with a subtotal that didn't match its own line
 * items. First attempt (via director) relabeled THIS line to "Security
 * System", reasoning T56 only bans naming alarm EQUIPMENT, not the word
 * "security" itself. **Luis corrected that live, same day: wrong — the word
 * "security" can't appear on the page at all, category name or not, that's
 * the whole point of the alarm-safe wording.** Reverted back to "Smart
 * Home". The actual fix for the heading collision is on the OTHER side
 * instead — MSElectricContract.tsx no longer prints the itemized section's
 * "Smart Home" heading, so this is the only "Smart Home" left on the page.
 *
 * The label is hard-coded rather than taken from `fieldToLabel` (which says
 * "Security System" for the dropdown/internal use) precisely because that
 * name must never reach this customer-facing line.
 *
 * It deliberately does NOT check `security_system_show`, unlike every other
 * scope line. This line exists for one reason: to account for money the TOTAL
 * is charging. `calcTotalCost` only skips a hidden category when it is called
 * with `deductDisabled`, and no contract passes it - so hiding a category
 * never stops it being charged. A guard here would therefore recreate the
 * exact defect the line was written to close, and did: James Sawkins
 * (2026-07-25, Caballero's) has `security_system_show === 0` and $2,117.98
 * charged, listed by nothing. Measured across all 7,151 customers, two records
 * are in that state and one of them is a test customer.
 *
 * Whether `_show` should stop the TOTAL charging is a separate question about
 * data nobody writes any more - tracked on its own ticket, not here.
 */
export const smartHomeScopeLine = (
  customer: schema.Customer,
): ScopeLine | null => {
  if (customer.security_system !== 'yes') return null

  const price = utils.parseFloating(customer.security_system_customer_cost)
  if (!Number.isFinite(price) || price <= 0) return null

  return { key: 'security_system', label: 'Smart Home', price }
}

export const calcTotalCost = ({
  customer,
  deductDisabled,
  twoStoryFee,
  type,
}: CalcTotalCostParams): number => {
  if (!customer) {
    // c.debug('useTotalCost() -> returning 0 as no customer found')
    return 0
  }

  const turnedOn = efficiencyKeys
    .filter((k) => customer[k] === 'yes')
    .filter((field) => {
      if (!deductDisabled) return true
      const showField =
        schema.normalizeCheckShows[field] ||
        ((field + '_show') as schema.CustomerField)
      return customer[showField] === 1
    })

  const costFields = turnedOn.map(
    (k) => k + (type === 'company' ? '_cost' : '_customer_cost'),
  )
  const costsStr = costFields.map(
    (k) => customer[k as schema.CustomerField],
  ) as utils.strings
  const costs = costsStr
    .map((cost) => utils.parseFloating(cost))
    // TODO: Is this really needed?
    .filter(Number.isFinite)

  let total = costs.reduce((acc, curr) => acc + curr, 0)

  // Must be mirrored in usePayment()
  if (
    env.CODENAME === 'nirvana' &&
    !customer.payment_is_applied &&
    type === 'customer'
  ) {
    if (customer.homeStoryNumber === '2') {
      total += utils.parseFloating(twoStoryFee)
    }
  }

  return total
}
